import React, { useState, useEffect, useRef, useMemo } from "react";
import styled from "styled-components";
import { db, storage } from "../firebase"; // adjust path to your existing firebase init
import { getAuth } from "firebase/auth";
import app from "../firebase";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import SimpleModal from "./SimpleModal";
import { buildSubmissionPreviewExperience } from "../utils/submissionDraftToExperience";
import {
  collection,
  addDoc,
  serverTimestamp,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  doc,
  setDoc,
  getDoc,
  deleteDoc,
} from "firebase/firestore";
import {
  PREVIEW_MAX_LENGTH,
  buildExperienceDescriptionPrompt,
  buildPreviewPrompt,
  buildHeroImagePrompt,
} from "./promptTemplates"; // adjust path to wherever you place promptTemplates.js

// ---------------------------------------------------------------------------
// This component replaces the existing "Share an Experience" submission form.
// It assumes the user is already authenticated by the time it mounts —
// wire it into the existing post-login flow, do not add auth logic here.
//
// Pass the authenticated user in as `currentUser` (shape: { uid, displayName,
// email, photoURL }) — it's used to prefill Creator, Studio, or Organization and to stamp
// submittedByUid / submittedByDisplayName / submittedByEmail /
// submittedByProfilePhoto on the Firestore doc. All fields are safe to omit
// (falls back to blank/null) but omitting them means submissions won't be
// attributable to a user.
//
// Render it inside whatever modal wrapper the app already uses
// (e.g. the existing SimpleModal pattern), passing an `onClose` handler.
//
// SCHEMA_VERSION bumped to 2 for this schema (format/themes/access/
// platforms split out, location model added, practical info added,
// experienceLink renamed to primaryExperienceLink). Treat this schema as a
// long-term contract — prefer adding new optional fields over
// renaming/removing existing ones, and bump SCHEMA_VERSION on breaking
// changes so old submissions stay interpretable.
// ---------------------------------------------------------------------------

const SCHEMA_VERSION = 2;

// Flip to true when the creator submission wizard is ready for everyone.
// All creator flow code stays in place; this only gates entry.
const CREATOR_SUBMISSION_ENABLED = false;

// Dev preview: add ?creatorPreview to the URL, or run in the console:
// localStorage.setItem('rc_creator_preview', '1') then refresh.
function isCreatorSubmissionOpen() {
  if (CREATOR_SUBMISSION_ENABLED) return true;
  if (typeof window === "undefined") return false;
  return (
    new URLSearchParams(window.location.search).has("creatorPreview") ||
    window.localStorage.getItem("rc_creator_preview") === "1"
  );
}
// Every future breaking schema change (renaming a field, changing what a
// slug means, changing required-ness in a way that invalidates old data)
// should increment SCHEMA_VERSION rather than silently reinterpreting
// historical submissions. Purely additive changes (new optional field, new
// option in an existing list) do not require a bump.

// ---------------------------------------------------------------------------
// Option lists — canonical slug + friendly label. Structured arrays, not
// freeform strings. This is the single source of truth for every
// dropdown/chip list in the form — never duplicate a label elsewhere.
//
// This is intended to be the long-term RealityCheck submission ontology.
// Prefer adding optional metadata over changing existing field semantics,
// and avoid creating a new field that duplicates the meaning of an
// existing one (e.g. don't add a second "how do people get this" field).
//
// Before making changes: check the whole ontology for internal
// consistency. Format, Themes, Access, Location Model, Compatible
// Platforms, Pricing, Availability, Recommended Audience, and Supported
// Languages should each answer a DIFFERENT question and should not overlap
// semantically. If two fields start answering the same question, that's a
// sign to collapse them, not add a third. Do not reintroduce WebAR or
// WebVR (see Format below). Do not simplify this back toward fewer,
// broader fields — the granularity here is deliberate, tested against a
// wide range of real experience types (XR, browser/interactive, spatial
// media, live performance, installations, museums, projection, fulldome,
// drone shows), and collapsing it will just recreate the ambiguity this
// schema was built to remove.
// ---------------------------------------------------------------------------

// ---------- Discovery: what is it, what's it about? ----------

// What type of immersive experience is this?
//
// WebAR/WebVR were deliberately removed in favor of pairing WebXR with
// VR/AR/MR: a VR-only browser experience is `vr` + `webxr`; a mobile
// browser AR experience is `ar` + `webxr`. Interactive 3D is for
// browser/desktop 3D that ISN'T XR at all (Three.js, Babylon.js, Unity
// WebGL, a WebGL museum walkthrough) — without it, creators building
// plain 3D web experiences had nowhere to go but WebXR, which misrepresents
// them as immersive/headset content.
const FORMAT_OPTIONS = [
  { slug: "vr", label: "Virtual Reality (VR)" },
  { slug: "ar", label: "Augmented Reality (AR)" },
  { slug: "mr", label: "Mixed Reality (MR)" },
  { slug: "webxr", label: "WebXR" },
  { slug: "interactive-3d", label: "Interactive 3D" },
  { slug: "social-virtual-worlds", label: "Social Virtual Worlds / Metaverse" },
  { slug: "spatial-360-media", label: "Spatial & 360 Media" },
  { slug: "lbe", label: "Location-Based Entertainment (LBE)" },
  { slug: "immersive-performance", label: "Immersive Performance" },
  { slug: "interactive-installation", label: "Interactive Installation" },
  { slug: "projection-mapping", label: "Projection Mapping" },
  { slug: "full-dome", label: "Full Dome Experience" },
  { slug: "museum-exhibition", label: "Museum / Exhibition" },
  { slug: "hybrid-physical-digital", label: "Hybrid Physical + Digital" },
  { slug: "live-event", label: "Live Event" },
  { slug: "drone-show", label: "Drone Show" },
];

// Deliberately no "Other" here (unlike Format's earlier draft) — Format is
// still a REQUIRED field, so removing the catch-all doesn't remove the
// obligation to pick something. The intent is: pick the closest fit, and
// use Creator Notes if it's a genuine edge case RealityCheck should know
// about. This is a curation signal, not a blocker — don't reintroduce
// "Other" here without also reconsidering whether Format should still be
// required.
const FORMAT_MAX = 3;

// ---------- Classification: what's it about, how do you get it? ----------

// What is this experience primarily about? Deliberately mixes genre words
// (Horror, Comedy, Sci-Fi) with subject/mood words (Climate, Wellness,
// Nature) — both answer "what is this about" for discovery purposes, even
// though they aren't taxonomically parallel.
const THEME_OPTIONS = [
  { slug: "adventure", label: "Adventure" },
  { slug: "art", label: "Art" },
  { slug: "climate", label: "Climate" },
  { slug: "comedy", label: "Comedy" },
  { slug: "concert", label: "Concert" },
  { slug: "dance", label: "Dance" },
  { slug: "documentary", label: "Documentary" },
  { slug: "educational", label: "Educational" },
  { slug: "escape", label: "Escape" },
  { slug: "family", label: "Family" },
  { slug: "fantasy", label: "Fantasy" },
  { slug: "history", label: "History" },
  { slug: "horror", label: "Horror" },
  { slug: "mystery", label: "Mystery" },
  { slug: "music", label: "Music" },
  { slug: "narrative", label: "Narrative" },
  { slug: "nature", label: "Nature" },
  { slug: "puzzle", label: "Puzzle" },
  { slug: "sci-fi", label: "Sci-Fi" },
  { slug: "social", label: "Social" },
  { slug: "sports", label: "Sports" },
  { slug: "theater", label: "Theater" },
  { slug: "thriller", label: "Thriller" },
  { slug: "wellness", label: "Wellness" },
  { slug: "other", label: "Other" },
];

const THEME_MAX = 5;

// How do people get it? No "Other" — ontology should be tight enough not
// to need one here (Format is the one place ambiguity is expected). Kept
// separate from Location Model and Platforms on purpose: Access answers
// "how do I get it," Location Model answers "where does it happen,"
// Platforms answers "what can I run it on." Don't collapse these back
// together even when a single example (e.g. Sandbox VR) touches all three.
const ACCESS_OPTIONS = [
  { slug: "browser-web", label: "Browser / Web" },
  { slug: "downloadable-app", label: "Downloadable App" },
  { slug: "content-platform", label: "Storefront / Content Platform" },
  { slug: "in-person", label: "In-Person" },
];

// Deliberately kept as one option rather than split into Storefront /
// Streaming — Netflix, Quest Store, SideQuest, Steam, itch.io, and the App
// Store all answer the same question ("where do I go to get this?"), even
// though some are storefronts (you download something) and some are
// destinations (you experience it right there).
const CONTENT_PLATFORM_HELP_TEXT =
  "Examples: Steam, Quest Store, SideQuest, itch.io, App Store, Google Play, Netflix, YouTube, and more.";

// Where does it happen? Solves the "Pokémon GO problem" — this is a
// location question, not a format or access question. Labels (not slugs)
// were revised because "Anywhere" was ambiguous — it read as if it meant
// the same thing for a GPS-driven AR game (Pokémon GO) and a VR headset
// title (Beat Saber), which are not the same claim. "Location-Flexible /
// On the Go" (slug still `anywhere`) is specifically about real-world,
// GPS/location-aware experiences; "At Home / Personal Space" (slug still
// `at-home`) is about private, device-based experiences with no location
// component at all. Multi-select on purpose: a headset title also
// playable at a fixed venue is legitimately both At Home and Fixed
// Location.
const LOCATION_MODEL_OPTIONS = [
  { slug: "anywhere", label: "Location-Flexible / On the Go" },
  { slug: "at-home", label: "At Home / Personal Space" },
  { slug: "fixed-location", label: "Fixed Location" },
  { slug: "multiple-locations", label: "Multiple Locations" },
];

// What can people actually run this on? Grouped by what someone already
// owns, not by rendering pipeline — PC VR and PC/Mac are kept distinct
// since RealityCheck also covers non-VR desktop and browser experiences.
// Deliberately NOT broken down to device-generation granularity (no
// "Quest 2" vs "Quest 3" vs "Quest 3S") — that level of detail belongs in
// future compatibility metadata, not creator-facing ontology.
const PLATFORM_OPTIONS = [
  // XR
  { slug: "meta-quest", label: "Meta Quest" },
  { slug: "apple-vision-pro", label: "Apple Vision Pro" },
  { slug: "pico", label: "Pico" },
  { slug: "playstation-vr", label: "PlayStation VR" },
  { slug: "pcvr", label: "PC VR (SteamVR-compatible)" },
  { slug: "android-xr", label: "Android XR" },
  // Mobile
  { slug: "mobile-ios", label: "iPhone / iPad (iOS)" },
  { slug: "mobile-android", label: "Android" },
  // Computer
  { slug: "pc", label: "PC" },
  { slug: "mac", label: "Mac" },
  // Browser
  { slug: "web-browser", label: "Web Browser" },
  // Physical
  { slug: "in-venue-equipment", label: "In-Venue Equipment" },
  { slug: "no-device-required", label: "No Personal Device Required" },
];

const PLAYER_OPTIONS = [
  { slug: "solo", label: "Solo" },
  { slug: "2-players", label: "2 Players" },
  { slug: "2-4-players", label: "2–4 Players" },
  { slug: "5-10-players", label: "5–10 Players" },
  { slug: "10-plus-players", label: "10+ Players" },
  { slug: "large-groups", label: "Large Groups" },
  { slug: "flexible", label: "Flexible" },
];

const DURATION_OPTIONS = [
  { slug: "under-5-min", label: "Under 5 minutes" },
  { slug: "5-15-min", label: "5–15 minutes" },
  { slug: "15-30-min", label: "15–30 minutes" },
  { slug: "30-60-min", label: "30–60 minutes" },
  { slug: "1-2-hours", label: "1–2 hours" },
  { slug: "2-4-hours", label: "2–4 hours" },
  { slug: "half-day", label: "Half Day" },
  { slug: "full-day", label: "Full Day" },
  { slug: "ongoing", label: "Ongoing" },
  { slug: "flexible", label: "Flexible" },
];

// ---------- Practical Information ----------

const PRICING_MODEL_OPTIONS = [
  { slug: "free", label: "Free" },
  { slug: "freemium", label: "Freemium" },
  { slug: "paid", label: "Paid" },
  { slug: "ticketed", label: "Ticketed" },
  { slug: "subscription", label: "Subscription" },
  { slug: "included-with-admission", label: "Included with Admission" },
  { slug: "contact-for-pricing", label: "Contact for Pricing" },
];

// No "Coming Soon" — that's a statement about the future, not the present
// state of availability, and it invites premature submissions for things
// that don't exist yet. If an available experience will also launch on
// another platform later, that nuance belongs in Availability Details
// ("Available now in venue, headset version coming soon"), not as a
// selectable state here.
const AVAILABILITY_OPTIONS = [
  { slug: "available-now", label: "Available Now" },
  { slug: "limited-run", label: "Limited Run" },
  { slug: "seasonal", label: "Seasonal" },
  { slug: "touring", label: "Touring" },
  { slug: "reservation-required", label: "Reservation Required" },
];

// No "All Ages" option — selecting all four bands already means all ages.
// Adding a fifth option that duplicates "select everything" invites
// inconsistent data (some creators picking All Ages, others picking all
// four bands, for the same meaning).
const RECOMMENDED_AUDIENCE_OPTIONS = [
  { slug: "young-children", label: "Young Children (3–7)" },
  { slug: "kids", label: "Kids (8–12)" },
  { slug: "teens", label: "Teens (13–17)" },
  { slug: "adults", label: "Adults (18+)" },
];

// Optional discovery filter, not a completeness requirement — language is
// hard to reliably infer later (unlike, say, duration), so it's collected
// upfront even though it's not required.
const SUPPORTED_LANGUAGE_OPTIONS = [
  { slug: "english", label: "English" },
  { slug: "spanish", label: "Spanish" },
  { slug: "french", label: "French" },
  { slug: "japanese", label: "Japanese" },
  { slug: "korean", label: "Korean" },
  { slug: "chinese", label: "Chinese" },
  { slug: "german", label: "German" },
  { slug: "italian", label: "Italian" },
  { slug: "portuguese", label: "Portuguese" },
  { slug: "non-verbal", label: "Non-verbal / No Language" },
  { slug: "other", label: "Other" },
];

// Accessibility, comfort rating, input method, interaction level, mood,
// and freeform keywords/tags are intentionally NOT collected from creators
// here — self-reported data in these categories tends to be unreliable or
// inconsistent, and they're better handled through curation or AI
// enrichment later (see TODOs below) than by asking creators to self-rate.

// TODO: Creator profiles (persistent identity across submissions, distinct
//       from the current per-submission creatorDisplayName text field)
// TODO: Multiple venue support (Location Model = Multiple Locations
//       currently collects one representative address only)
// TODO: Multiple experience links (Website, Ticket URL, App Store, Steam,
//       Quest, trailer, Instagram, Discord, etc. — primaryExperienceLink
//       is intentionally singular for now)
// TODO: AI-generated metadata enrichment (auto-suggested themes, tags,
//       generated descriptions/previews/artwork moving from "coming soon"
//       prompts to native generation)
// TODO: Platform compatibility auto-detection
// TODO: Moderation / admin review flow (status is currently always
//       "pending_review" with no reviewer-facing tooling yet)
// TODO: Creator analytics

// ---------------------------------------------------------------------------
// Lightweight keyword-based suggestions — NOT real AI. This is a stopgap
// ahead of the "AI-generated metadata enrichment" TODO above: it scans the
// creator's own Marketing/Experience Description text for plain keyword
// matches and pre-selects the corresponding Format/Theme/Access/Location
// Model/Platform chips so creators aren't starting from zero on Experience
// Details. It only ever ADDS to empty-or-partial selections — it never
// removes or overrides a chip the creator already picked, and it runs
// exactly once (the first time Experience Details is reached), not on
// every keystroke. Replace with real AI-driven suggestion later without
// needing to change how the wizard consumes the result (still just arrays
// of slugs).
const FORMAT_KEYWORDS = {
  vr: ["virtual reality", "vr headset", "meta quest", "oculus", "vision pro", "steamvr", "vr experience"],
  ar: ["augmented reality", "ar app", "camera overlay", "ar experience"],
  mr: ["mixed reality"],
  webxr: ["webxr", "browser-based vr", "browser-based ar", "browser vr", "browser ar"],
  "interactive-3d": ["three.js", "webgl", "babylon.js", "playcanvas", "3d website", "interactive 3d"],
  "social-virtual-worlds": ["metaverse", "social vr", "virtual world", "virtual worlds"],
  "spatial-360-media": ["360 video", "360-degree", "360 degree", "spatial video", "volumetric", "spatial photo"],
  lbe: ["location-based", "location based entertainment", "venue-based"],
  "immersive-performance": ["immersive theater", "immersive theatre", "immersive performance", "interactive performance"],
  "interactive-installation": ["installation", "interactive exhibit", "interactive art"],
  "projection-mapping": ["projection mapping", "projected onto", "projection-mapped"],
  "full-dome": ["planetarium", "dome film", "fulldome", "full dome"],
  "museum-exhibition": ["museum", "exhibition", "exhibit"],
  "hybrid-physical-digital": ["hybrid experience", "physical and digital"],
  "live-event": ["live event", "live show"],
  "drone-show": ["drone show", "drone display"],
};

const THEME_KEYWORDS = {
  adventure: ["adventure"],
  art: ["art installation", "artistic", "art piece"],
  climate: ["climate", "environment", "sustainability", "sustainable"],
  comedy: ["comedy", "funny", "humor", "humorous"],
  concert: ["concert", "live music performance"],
  dance: ["dance", "choreography", "choreographed"],
  documentary: ["documentary", "true story", "based on true events"],
  educational: ["educational", "learn about", "teaches", "teaching"],
  escape: ["escape room", "escape the"],
  family: ["family-friendly", "for the whole family", "kids and family"],
  fantasy: ["fantasy", "mythical", "magical world"],
  history: ["historical", "history of"],
  horror: ["horror", "scary", "haunted", "terrifying"],
  mystery: ["mystery", "whodunit", "detective"],
  music: ["music", "musical", "song"],
  narrative: ["story", "narrative", "storytelling", "story-driven"],
  nature: ["nature", "wildlife", "forest", "ocean", "wilderness"],
  puzzle: ["puzzle", "brain teaser", "solve clues", "solving puzzles"],
  "sci-fi": ["sci-fi", "science fiction", "futuristic"],
  social: ["social experience", "multiplayer", "with friends"],
  sports: ["sports", "athletic", "athletics"],
  theater: ["theater", "theatre"],
  thriller: ["thriller", "suspense", "suspenseful"],
  wellness: ["wellness", "meditation", "relaxation", "mindfulness"],
};

const ACCESS_KEYWORDS = {
  "browser-web": ["in your browser", "web browser", "no download", "play online"],
  "downloadable-app": ["download the app", "downloadable", "available for download"],
  "content-platform": ["on steam", "quest store", "sidequest", "itch.io", "app store", "google play", "on netflix"],
  "in-person": ["in-person", "at our venue", "visit us", "on-site"],
};

const LOCATION_MODEL_KEYWORDS = {
  anywhere: ["anywhere", "wherever you are", "play anywhere"],
  "at-home": ["at home", "from home", "in your living room"],
  "fixed-location": ["at our venue", "visit our", "located at", "our location"],
  "multiple-locations": ["multiple locations", "touring", "various cities", "several venues"],
};

const PLATFORM_KEYWORDS = {
  "meta-quest": ["meta quest", "oculus quest"],
  "apple-vision-pro": ["vision pro", "apple vision"],
  pico: ["pico headset", "pico 4"],
  "playstation-vr": ["playstation vr", "psvr"],
  pcvr: ["steamvr", "pc vr", "pcvr"],
  "android-xr": ["android xr"],
  "mobile-ios": ["ios app", "iphone", "ipad"],
  "mobile-android": ["android app", "android phone"],
  pc: ["windows pc", "for pc"],
  mac: ["for mac", "macos"],
  "web-browser": ["web browser", "in your browser"],
};

function suggestSlugsFromText(text, keywordMap, max = 0) {
  const lower = text.toLowerCase();
  const matches = Object.entries(keywordMap)
    .filter(([, keywords]) => keywords.some((kw) => lower.includes(kw)))
    .map(([slug]) => slug);
  return max > 0 ? matches.slice(0, max) : matches;
}

// Best-effort, non-blocking parse of a pasted location block into the
// legacy structured fields (kept for backend compatibility — see the
// state declarations in CreatorWizard). Only handles the common
// "Venue Name / Street Address / City, State ZIP" three-line shape.
// Anything else is left blank rather than guessed at — a wrong guess is
// worse than no data, and locationText is what's actually authoritative
// and displayed. Never throws; always returns a fully-shaped object.
function parseLocationText(text) {
  const empty = { venueName: null, streetAddress: null, city: null, stateRegion: null, country: null };
  try {
    const lines = (text || "")
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean);
    if (lines.length < 3) return empty;

    const [venueLine, streetLine, cityStateLine] = lines;
    const match = cityStateLine.match(/^([^,]+),\s*([A-Za-z]{2,})\s*\d{0,10}$/);
    if (!match) return { ...empty, venueName: venueLine, streetAddress: streetLine };

    return {
      venueName: venueLine || null,
      streetAddress: streetLine || null,
      city: match[1].trim() || null,
      stateRegion: match[2].trim() || null,
      country: null,
    };
  } catch (err) {
    console.error("Location text parse failed (non-blocking)", err);
    return empty;
  }
}

const CREATOR_STEPS = [
  "Basics",
  "Description",
  "Images",
  "Experience Information",
  "Review",
];

// ---------------------------------------------------------------------------
// Question-accordion system for the merged Experience Information step.
// Each collapsible section (Experience, Access, Availability, Audience,
// Location, Additional Information) contains individual "questions" —
// only one open per section at a time. Completing the open question
// auto-advances to the next incomplete one in the section; all-complete
// collapses the whole section down to summary rows. Nothing is ever
// locked — clicking any question header (complete or not) reopens it.
// ---------------------------------------------------------------------------

const SECTION_FIELD_ORDER = {
  experience: ["format", "themes", "players", "duration"],
  access: ["accessMethod", "locationModel", "platforms", "platformNotes"],
  availability: ["pricingModel", "priceDetails", "availability", "availabilityDetails"],
  audience: ["recommendedAudience", "supportedLanguages"],
  location: ["primaryLocation"],
  additional: ["contentNotes", "creatorNotes"],
};

// Maps a question's field key to the key it uses in the `errors` object
// from getStepErrors, so a validation failure can auto-open the right
// question. Fields with no key here have no validation (always optional).
const ERROR_KEY_FOR_FIELD = {
  format: "formats",
  themes: "themes",
  players: "players",
  duration: "duration",
  accessMethod: "accessMethods",
  locationModel: "locationModels",
  platforms: "platforms",
  pricingModel: "pricingModels",
  priceDetails: "priceDetails",
  availability: "availability",
  recommendedAudience: "recommendedAudiences",
  supportedLanguages: "supportedLanguages",
  primaryLocation: "locationText",
};

// Platform chips grouped by what someone already owns, so the picker
// reads as four small lists instead of one wall of 13 chips. Purely a
// display grouping — selection/storage is still one flat `platforms`
// array of slugs, same as before.
const PLATFORM_GROUPS = [
  { label: "XR Headsets", slugs: ["meta-quest", "apple-vision-pro", "pico", "playstation-vr", "pcvr", "android-xr"] },
  { label: "Mobile", slugs: ["mobile-ios", "mobile-android"] },
  { label: "Computer", slugs: ["pc", "mac", "web-browser"] },
  { label: "Venue", slugs: ["in-venue-equipment", "no-device-required"] },
];

const NOTES_PLACEHOLDER =
  "Why did you like it? Who's it for? What makes it different?";

const IMAGE_STYLE_OPTIONS = [
  { slug: "photorealistic", label: "Photorealistic" },
  { slug: "stylized", label: "Stylized" },
  { slug: "illustrated", label: "Illustrated" },
  { slug: "cinematic", label: "Cinematic" },
  { slug: "no-preference", label: "No Preference" },
];

// ---------------------------------------------------------------------------
// Styled components — minimal, functional. Swap literals for your theme
// tokens/ThemeProvider if you have one.
// ---------------------------------------------------------------------------

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 28px;
  max-width: ${(props) => (props.$wide ? "820px" : "640px")};
`;

const StepHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const StepTitle = styled.h2`
  font-size: 22px;
  font-weight: 600;
  margin: 0;
  color: #f4f1fb;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const AccentNote = styled.div`
  font-size: 14px;
  color: #00f0b0;
  background: rgba(0, 240, 176, 0.08);
  border: 1px solid rgba(0, 240, 176, 0.25);
  border-radius: 8px;
  padding: 10px 12px;
`;

const LabelRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
`;

const Label = styled.label`
  font-size: 16px;
  font-weight: 600;
  color: #f4f1fb;
`;

const OptionalTag = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: #8579a8;
  margin-left: 6px;
`;

const Required = styled.span`
  display: inline-block;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #0a0712;
  background: #00f0b0;
  border-radius: 4px;
  padding: 1.5px 6px;
  margin-left: 8px;
  vertical-align: middle;
`;

const HelpText = styled.p`
  font-size: 14px;
  color: #b3a8cf;
  margin: 0;
`;

const RoleSubtitle = styled.p`
  font-size: 16px;
  line-height: 1.55;
  color: #ebe6f7;
  margin: 0;
  max-width: 36rem;
`;

const RoleSelectStage = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  min-height: calc(100dvh - 96px);
  padding: 24px 0;
`;

const RoleSelectWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 36px;
  width: 100%;
  max-width: 520px;
  margin: 0 auto;
`;

const RoleSelectIntro = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  max-width: 28rem;
`;

const RoleSelectTitle = styled.h2`
  margin: 0;
  font-size: clamp(1.85rem, 5.5vw, 2.5rem);
  font-weight: 700;
  line-height: 1.2;
  color: #f4f1fb;
  text-align: center;
`;

const RoleSelectSubtitle = styled(RoleSubtitle)`
  margin-top: 12px;
  text-align: center;
`;

const RoleSelectGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  width: 100%;
  max-width: 420px;

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
    max-width: 280px;
  }
`;

const RoleSelectCard = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 12px;
  background: #150e24;
  border: 1px solid #2c2245;
  border-radius: 16px;
  padding: 32px 20px;
  min-height: 148px;
  cursor: pointer;
  color: #f4f1fb;
  transition: border-color 0.15s ease, background 0.15s ease;

  &:hover {
    border-color: #00f0b0;
    background: rgba(0, 240, 176, 0.06);
  }
`;

const RoleSelectEmoji = styled.div`
  font-size: 36px;
  line-height: 1;
`;

const RoleSelectLabel = styled.div`
  font-size: 16px;
  font-weight: 600;
  line-height: 1.3;
`;

const CharCount = styled.span`
  font-size: 12px;
  color: #6c6188;
`;

const TextInput = styled.input`
  background: #0f0a1c;
  border: 1px solid #2c2245;
  border-radius: 8px;
  color: #f4f1fb;
  font-size: 14px;
  padding: 10px 12px;

  &:focus {
    outline: none;
    border-color: #00f0b0;
  }
`;

const TextArea = styled.textarea`
  background: #0f0a1c;
  border: 1px solid #2c2245;
  border-radius: 8px;
  color: #f4f1fb;
  font-size: 14px;
  padding: 10px 12px;
  min-height: ${(props) => (props.$short ? "70px" : "110px")};
  resize: vertical;

  &:focus {
    outline: none;
    border-color: #00f0b0;
  }
`;

const ErrorText = styled.span`
  font-size: 13px;
  color: #ff6b6b;
`;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Chip = styled.button`
  font-size: 14px;
  font-weight: 500;
  color: ${(props) => (props.$selected ? "#00f0b0" : "#9c90bc")};
  background: ${(props) => (props.$selected ? "rgba(0,240,176,0.12)" : "#0f0a1c")};
  border: 1px solid ${(props) => (props.$selected ? "#00f0b0" : "#2c2245")};
  border-radius: 100px;
  padding: 8px 14px;
  cursor: ${(props) => (props.$disabled ? "not-allowed" : "pointer")};
  opacity: ${(props) => (props.$disabled ? 0.4 : 1)};
  transition: border-color 0.15s, color 0.15s;

  &:hover {
    border-color: ${(props) => (!props.$disabled && !props.$selected ? "#473a6b" : undefined)};
    color: ${(props) => (!props.$disabled && !props.$selected ? "#c9bfe0" : undefined)};
  }
`;

const PipelineArrow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #6c6188;
  font-size: 12px;
  margin: -8px 0;
  padding-left: 4px;

  &::before {
    content: "↓";
    color: #00f0b0;
    font-size: 14px;
  }
`;

const Dropzone = styled.label`
  border: 1.5px dashed ${(props) => (props.$dragging ? "#00f0b0" : props.$filled ? "#00f0b0" : "#2c2245")};
  border-radius: 10px;
  padding: 28px 16px;
  text-align: center;
  cursor: pointer;
  display: block;
  background: ${(props) => (props.$dragging ? "rgba(0, 240, 176, 0.06)" : "transparent")};

  input {
    display: none;
  }
`;

const DropzoneTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #f4f1fb;
`;

const DropzoneSub = styled.div`
  font-size: 12px;
  color: #6c6188;
  margin-top: 4px;
`;

const HeroPreviewImg = styled.img`
  width: 100%;
  max-height: 220px;
  object-fit: cover;
  border-radius: 10px;
  border: 1px solid #2c2245;
`;

const AiHelperRow = styled.div`
  margin-top: 4px;
  padding-top: 10px;
  border-top: 1px dashed #2c2245;
`;

const AiHelperLabel = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #ff4fd8;
  margin-bottom: 2px;
`;

const AiHelperDesc = styled.p`
  font-size: 13px;
  color: #9c90bc;
  margin: 0 0 8px;
`;

const AiHelperSuccess = styled.div`
  font-size: 13px;
  color: #00f0b0;
  margin-top: 8px;
`;

// Lightweight utility attached to an output field — not a separate step
// in the form. Never expands or displays the prompt text in the UI; it
// just copies straight to the clipboard, since showing the raw prompt
// inline is what made the old AiHelperCard feel like its own section.
function AiPromptHelper({ label, description, promptText }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(promptText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (err) {
      console.error("Copy failed", err);
    }
  };

  return (
    <AiHelperRow>
      <AiHelperLabel>📋 AI Helper: {label}</AiHelperLabel>
      <AiHelperDesc>{description}</AiHelperDesc>
      <CopyButton type="button" onClick={handleCopy}>
        Copy AI Prompt
      </CopyButton>
      {copied && <AiHelperSuccess>✓ Prompt copied. Paste it into ChatGPT.</AiHelperSuccess>}
    </AiHelperRow>
  );
}

const CopyButton = styled.button`
  font-size: 13px;
  font-weight: 600;
  color: #0a0712;
  background: #ff4fd8;
  border: none;
  border-radius: 6px;
  padding: 8px 14px;
  cursor: pointer;

  &:hover {
    opacity: 0.85;
  }
`;

const NavRow = styled.div`
  display: flex;
  justify-content: ${(props) => (props.$end ? "flex-end" : "space-between")};
  align-items: center;
`;

const TopNavRow = styled.div`
  display: flex;
  justify-content: flex-start;
`;

const SecondaryButton = styled.button`
  font-size: 14px;
  font-weight: 600;
  color: #c9bfe0;
  background: rgba(255, 255, 255, 0.03);
  border: 1.5px solid #473a6b;
  border-radius: 10px;
  padding: 12px 20px;
  cursor: pointer;

  &:hover {
    border-color: #6c5a9e;
    background: rgba(255, 255, 255, 0.06);
  }
`;

const PrimaryButton = styled.button`
  font-size: 14px;
  font-weight: 600;
  color: #0a0712;
  background: #00f0b0;
  border: none;
  border-radius: 10px;
  padding: 12px 24px;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const RoleGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const RoleCard = styled.button`
  text-align: left;
  background: #150e24;
  border: 1px solid #2c2245;
  border-radius: 14px;
  padding: 24px 20px;
  cursor: pointer;
  color: #f4f1fb;

  &:hover {
    border-color: #00f0b0;
  }
`;

const RoleEmoji = styled.div`
  font-size: 26px;
  margin-bottom: 10px;
`;

const RoleTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  margin-bottom: 6px;
`;

const SuccessWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 18px;
`;

const SuccessTitle = styled.h2`
  font-size: 20px;
  font-weight: 700;
  margin: 0;
  color: #f4f1fb;
`;

const SuccessButtons = styled.div`
  display: flex;
  gap: 12px;
`;

// ---------- Segmented progress indicator ----------

const ProgressRow = styled.div`
  display: flex;
  align-items: flex-start;
`;

const ProgressSegment = styled.div`
  display: flex;
  align-items: flex-start;
  flex: ${(props) => (props.$isLast ? "0" : "1")};
  padding-top: 12px;
`;

const ProgressStep = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  padding: 8px 6px;
  border-radius: 10px;
  cursor: ${(props) => (props.$clickable ? "pointer" : "default")};
  transition: background 0.15s;

  &:hover {
    background: ${(props) => (props.$clickable ? "rgba(0, 240, 176, 0.08)" : "transparent")};
  }
`;

const Dot = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${(props) => (props.$filled ? "#00f0b0" : "transparent")};
  border: 2.5px solid ${(props) => (props.$filled ? "#00f0b0" : "#3a2f5c")};
  transition: border-color 0.15s, background 0.15s;

  ${ProgressStep}:hover & {
    border-color: ${(props) => (props.$clickable ? "#00f0b0" : undefined)};
  }
`;

const Line = styled.div`
  flex: 1;
  height: 3px;
  margin: 18px 6px 0;
  border-radius: 2px;
  background: ${(props) => (props.$filled ? "#00f0b0" : "#2c2245")};
`;

const ProgressLabel = styled.span`
  font-size: 14px;
  color: ${(props) => (props.$active ? "#00f0b0" : "#9c90bc")};
  font-weight: ${(props) => (props.$active ? "700" : "500")};
  max-width: 92px;
  text-align: center;
  line-height: 1.25;

  ${ProgressStep}:hover & {
    color: ${(props) => (props.$clickable ? "#00f0b0" : undefined)};
  }
`;

const StepCountText = styled.div`
  font-size: 13px;
  color: #9c90bc;
  margin-bottom: 10px;
`;

// steps/currentIndex/onStepClick: every step is always clickable so creators
// can fill sections in any order. Submit is gated separately.
function ProgressBar({ steps, currentIndex, onStepClick }) {
  return (
    <div>
      <StepCountText>
        Step {currentIndex + 1} of {steps.length} · {steps[currentIndex]}
      </StepCountText>
      <ProgressRow>
        {steps.map((label, i) => {
          const clickable = i !== currentIndex;
          return (
            <ProgressSegment key={label} $isLast={i === steps.length - 1}>
              <ProgressStep
                type="button"
                $clickable={clickable}
                disabled={!clickable}
                onClick={clickable ? () => onStepClick?.(i) : undefined}
                aria-label={`Go to ${label}`}
              >
                <Dot $filled={i <= currentIndex} $clickable={clickable} />
                <ProgressLabel $active={i === currentIndex} $clickable={clickable}>
                  {label}
                </ProgressLabel>
              </ProgressStep>
              {i < steps.length - 1 && <Line $filled={i < currentIndex} />}
            </ProgressSegment>
          );
        })}
      </ProgressRow>
    </div>
  );
}

// ---------- Mini experience-card review ----------

const ReviewCard = styled.div`
  background: #150e24;
  border: 1px solid #2c2245;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
`;

const ReviewHero = styled.div`
  width: 100%;
  aspect-ratio: 21 / 8;
  background: #1b1230;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6c6188;
  font-size: 12px;
  cursor: pointer;

  &:hover {
    outline: 1.5px solid #00f0b0;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ReviewBody = styled.div`
  padding: 20px 22px 22px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ReviewTagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  cursor: pointer;
`;

const ReviewTitleBlock = styled.div`
  cursor: pointer;
  border-radius: 6px;

  &:hover {
    color: #00f0b0;
  }
`;

const ReviewTitle = styled.div`
  font-size: 22px;
  font-weight: 700;
  color: #f4f1fb;
`;

const ReviewCreator = styled.div`
  font-size: 14px;
  color: #6c6188;
`;

const ReviewPreview = styled.div`
  font-size: 16px;
  color: #c9bfe0;
  line-height: 1.5;
  cursor: pointer;

  &:hover {
    color: #f4f1fb;
  }
`;

// ---------- Listing-style review card sections ----------

const ReviewSectionEyebrow = styled.div`
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #6c6188;
  margin-top: 14px;
  margin-bottom: 2px;
`;

const ReviewInfoLine = styled.div`
  font-size: 15px;
  color: #c9bfe0;
  cursor: pointer;
  border-radius: 6px;

  &:hover {
    color: #00f0b0;
  }
`;

const ReviewInfoLabel = styled.span`
  color: #6c6188;
  margin-right: 6px;
`;

const ChecklistItem = styled.div`
  font-size: 15px;
  color: #c9bfe0;
  display: flex;
  align-items: baseline;
  gap: 6px;

  &::before {
    content: "✓";
    color: #00f0b0;
    font-size: 13px;
  }
`;

// ---------- Listing-page elements, modeled on the real RealityCheck
// experience page screenshots (hero → title → access lines → summary
// pill → availability → preview → glance grid → best-for → launch →
// gallery → about). Still a hand-built approximation, not the real
// component — see the TODO above the ReviewCard render — but built to
// match that page's actual visual language as closely as plain CSS
// allows, rather than an invented layout. ----------

const ListingCategoryLine = styled.div`
  font-size: 12.5px;
  color: #6c6188;
  cursor: pointer;

  &:hover {
    color: #00f0b0;
  }
`;

const ListingPrimaryLine = styled.div`
  font-size: 17px;
  font-weight: 700;
  color: #f4f1fb;
  cursor: pointer;

  &:hover {
    color: #00f0b0;
  }
`;

const ListingSecondaryLine = styled.div`
  font-size: 14px;
  color: #9c90bc;
  cursor: pointer;

  &:hover {
    color: #c9bfe0;
  }
`;

const ListingPill = styled.div`
  display: inline-block;
  font-size: 13px;
  font-weight: 600;
  color: #c9bfe0;
  border: 1px solid #3a2f5c;
  border-radius: 100px;
  padding: 7px 16px;
  margin-top: 4px;
  cursor: pointer;

  &:hover {
    border-color: #00f0b0;
    color: #00f0b0;
  }
`;

const AvailabilityRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #c9bfe0;
  cursor: pointer;

  &:hover {
    color: #00f0b0;
  }
`;

const AvailabilityDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #00f0b0;
  flex-shrink: 0;
`;

const GlanceGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px 24px;
  cursor: pointer;
  border-radius: 6px;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }

  &:hover {
    color: #00f0b0;
  }
`;

const BestForChip = styled.span`
  font-size: 13px;
  color: #c9bfe0;
  border: 1px solid #3a2f5c;
  border-radius: 100px;
  padding: 5px 12px;
`;

const LaunchButton = styled.div`
  text-align: center;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #fff;
  background: linear-gradient(90deg, #ff4fd8, #7c6fe8, #4fd8ff);
  border-radius: 12px;
  padding: 16px;
  opacity: 0.9;
`;

const LaunchCaption = styled.div`
  text-align: center;
  font-size: 12.5px;
  color: #6c6188;
  margin-top: 8px;
`;

const ListingDivider = styled.div`
  border-top: 1px solid #2c2245;
  margin: 4px 0;
`;

const GalleryRow = styled.div`
  display: flex;
  gap: 8px;
  overflow-x: auto;
  cursor: pointer;
  border-radius: 6px;

  &:hover img {
    outline: 1.5px solid #00f0b0;
  }
`;

const GalleryImg = styled.img`
  width: 140px;
  height: 90px;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid #2c2245;
  flex-shrink: 0;
`;

const AboutText = styled.p`
  font-size: 15px;
  color: #c9bfe0;
  line-height: 1.6;
  margin: 0;
  white-space: pre-wrap;
  cursor: pointer;

  &:hover {
    color: #f4f1fb;
  }
`;

const ReadyToSubmitCard = styled.div`
  border: 1px solid #2c2245;
  border-radius: 12px;
  background: rgba(0, 240, 176, 0.04);
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ReadyToSubmitTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #00f0b0;
`;

const CreatorGateOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 3000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(8, 0, 18, 0.88);
  backdrop-filter: blur(10px);
  padding: 24px;
  overflow-y: auto;
`;

const CreatorGatePanel = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 64px;
  width: min(52rem, 100%);
`;

const CreatorGateMessage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  text-align: center;
`;

const CreatorGateTitle = styled.div`
  font-size: clamp(2.75rem, 9vw, 5.5rem);
  font-weight: 800;
  letter-spacing: 0.02em;
  line-height: 1.08;
  background: linear-gradient(135deg, #ff00aa 0%, #b400ff 40%, #00f0ff 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  filter: drop-shadow(0 0 40px rgba(255, 0, 170, 0.45));
`;

const CreatorGateSub = styled.div`
  font-size: clamp(1.5rem, 6vw, 2.75rem);
  font-weight: 600;
  color: #ebe6f7;
  letter-spacing: 0.01em;
  line-height: 1.2;
`;

const CreatorGateFormCard = styled.form`
  width: 100%;
  background: rgba(22, 14, 38, 0.96);
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 14px;
  padding: 14px 16px 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.28);
`;

const CreatorGateFormIntro = styled.p`
  margin: 0;
  font-size: 16px;
  line-height: 1.55;
  color: #f4f1fb;
  text-align: center;
  max-width: 34rem;
  align-self: center;
`;

const CreatorGateFormRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;

  @media (min-width: 640px) {
    flex-direction: row;
    align-items: flex-end;
    gap: 10px;
  }
`;

const CreatorGateField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-width: 0;

  &:nth-of-type(3) {
    flex: 1.35;
  }
`;

const CreatorGateFieldLabel = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #f4f1fb;
`;

const CreatorGateInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  background: #171024;
  border: 1px solid #4a3d6b;
  border-radius: 8px;
  color: #ffffff;
  font-size: 14px;
  padding: 10px 12px;

  &:focus {
    outline: none;
    border-color: #00f0b0;
  }

  &::placeholder {
    color: #b3a8cf;
  }
`;

const CreatorGateSubmit = styled.button`
  width: 100%;
  font-size: 14px;
  font-weight: 600;
  color: #0a0712;
  background: linear-gradient(135deg, #00f0b0 0%, #00d4ff 100%);
  border: none;
  border-radius: 8px;
  padding: 10px 18px;
  cursor: pointer;
  transition: opacity 0.15s ease;
  white-space: nowrap;

  @media (min-width: 640px) {
    width: auto;
    flex: 0 0 auto;
  }

  &:hover:not(:disabled) {
    opacity: 0.92;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CreatorGateFormError = styled.div`
  width: 100%;
  text-align: center;
`;

const CreatorGateSuccessBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  text-align: center;
  width: 100%;
`;

const CreatorGateSuccessTitle = styled.p`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  line-height: 1.4;
  color: #00f0b0;
`;

const CreatorGateSuccessPrompt = styled.p`
  margin: 0;
  font-size: 15px;
  line-height: 1.5;
  color: rgba(244, 241, 251, 0.88);
`;

const CreatorGateAccountButton = styled.button`
  font-size: 14px;
  font-weight: 600;
  color: #fff;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.22);
  border-radius: 999px;
  padding: 10px 20px;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;

  &:hover {
    border-color: rgba(0, 240, 176, 0.55);
    background: rgba(0, 240, 176, 0.1);
  }
`;

function signedInAuthor() {
  const user = getAuth(app).currentUser;
  if (!user) return null;
  return {
    uid: user.uid,
    displayName: user.displayName || null,
    email: user.email || null,
    photoURL: user.photoURL || null,
  };
}

function submissionErrorMessage(err) {
  const code = err && err.code ? ` (${err.code})` : "";
  return `${(err && err.message) || "Something went wrong. Try again."}${code}`;
}

function withoutUndefined(value) {
  if (Array.isArray(value)) return value.map(withoutUndefined);
  if (value && typeof value === "object" && Object.getPrototypeOf(value) === Object.prototype) {
    return Object.fromEntries(
      Object.entries(value)
        .filter(([, entry]) => entry !== undefined)
        .map(([key, entry]) => [key, withoutUndefined(entry)])
    );
  }
  return value;
}

function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

function isValidExperienceUrl(value) {
  const trimmed = value.trim();
  if (!trimmed) return false;

  // Accept bare domains (e.g. realitycheck.online) and full URLs.
  const domainOrUrl = /^(?:https?:\/\/)?(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}(?:[/?#][^\s]*)?$/i;
  if (domainOrUrl.test(trimmed)) return true;

  try {
    const normalized = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
    const url = new URL(normalized);
    return !!url.hostname && url.hostname.includes(".");
  } catch {
    return false;
  }
}

function normalizeExperienceUrl(value) {
  const trimmed = value.trim();
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
}

function CreatorComingSoonOverlay({ onClose, currentUser, onRequestSignIn }) {
  const [name, setName] = useState(currentUser?.displayName ?? "");
  const [email, setEmail] = useState(currentUser?.email ?? "");
  const [experienceUrl, setExperienceUrl] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const trimmedName = name.trim();
    const trimmedEmail = email.trim();
    const trimmedUrl = experienceUrl.trim();

    if (!trimmedName) {
      setError("Please enter your name.");
      return;
    }
    if (!isValidEmail(trimmedEmail)) {
      setError("Please enter a valid email.");
      return;
    }
    if (!isValidExperienceUrl(trimmedUrl)) {
      setError("Enter a website URL like realitycheck.online or https://yoursite.com.");
      return;
    }
    setSubmitting(true);
    try {
      const normalizedUrl = normalizeExperienceUrl(trimmedUrl);
      await addDoc(collection(db, "creator_waitlist"), {
        name: trimmedName,
        email: trimmedEmail,
        experienceUrl: normalizedUrl,
        submittedByUid: currentUser?.uid ?? null,
        createdAt: serverTimestamp(),
      });
      setSubmitted(true);
    } catch (err) {
      console.error("Creator waitlist submission failed", err);
      setError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <CreatorGateOverlay
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Creator submission coming soon"
    >
      <CreatorGatePanel onClick={(e) => e.stopPropagation()}>
        <CreatorGateMessage>
          <CreatorGateTitle>Under construction...</CreatorGateTitle>
        </CreatorGateMessage>

        <CreatorGateFormCard onSubmit={handleSubmit} noValidate>
          {submitted ? (
            <CreatorGateSuccessBlock>
              <CreatorGateSuccessTitle>Submission received!</CreatorGateSuccessTitle>
              {!currentUser?.uid && (
                <>
                  <CreatorGateSuccessPrompt>Want to manage your submissions?</CreatorGateSuccessPrompt>
                  <CreatorGateAccountButton
                    type="button"
                    onClick={() =>
                      onRequestSignIn?.("signup", {
                        name: name.trim(),
                        email: email.trim(),
                      })
                    }
                  >
                    Create an account
                  </CreatorGateAccountButton>
                </>
              )}
            </CreatorGateSuccessBlock>
          ) : (
            <>
              <CreatorGateFormIntro>
                Have an experience to share? Leave your details and we&apos;ll be in touch.
              </CreatorGateFormIntro>

              <CreatorGateFormRow>
                <CreatorGateField>
                  <CreatorGateFieldLabel htmlFor="creator-waitlist-name">Name</CreatorGateFieldLabel>
                  <CreatorGateInput
                    id="creator-waitlist-name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                    autoComplete="name"
                  />
                </CreatorGateField>

                <CreatorGateField>
                  <CreatorGateFieldLabel htmlFor="creator-waitlist-email">Email</CreatorGateFieldLabel>
                  <CreatorGateInput
                    id="creator-waitlist-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    autoComplete="email"
                  />
                </CreatorGateField>

                <CreatorGateField>
                  <CreatorGateFieldLabel htmlFor="creator-waitlist-url">Experience URL</CreatorGateFieldLabel>
                  <CreatorGateInput
                    id="creator-waitlist-url"
                    type="text"
                    inputMode="url"
                    value={experienceUrl}
                    onChange={(e) => setExperienceUrl(e.target.value)}
                    placeholder="realitycheck.online"
                    autoComplete="url"
                  />
                </CreatorGateField>

                <CreatorGateSubmit type="submit" disabled={submitting}>
                  {submitting ? "Submitting..." : "Submit"}
                </CreatorGateSubmit>
              </CreatorGateFormRow>

              {error && (
                <CreatorGateFormError>
                  <ErrorText>{error}</ErrorText>
                </CreatorGateFormError>
              )}
            </>
          )}
        </CreatorGateFormCard>
      </CreatorGatePanel>
    </CreatorGateOverlay>
  );
}

// ---------------------------------------------------------------------------
// Reusable multi-select chips
// ---------------------------------------------------------------------------

const CollapsibleWrap = styled.div`
  border-top: 1px solid #2c2245;
  padding-top: 28px;
  margin-top: 4px;

  &:first-of-type {
    border-top: none;
    padding-top: 0;
    margin-top: 0;
  }
`;

const CollapsibleHeader = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  background: none;
  border: none;
  padding: 0 0 16px;
  cursor: pointer;
  text-align: left;
  border-radius: 8px;
  transition: opacity 0.15s;

  &:hover {
    opacity: 0.75;
  }
`;

const CollapsibleChevron = styled.span`
  font-size: 12px;
  color: #6c6188;
  transition: transform 0.15s;
  transform: rotate(${(props) => (props.$expanded ? "0deg" : "-90deg")});
  flex-shrink: 0;
`;

const CollapsibleTitle = styled.span`
  font-size: 18px;
  font-weight: 700;
  color: #f4f1fb;
`;

const CollapsibleBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding-bottom: 4px;
`;

// Collapsed by default; creators open each group as they work through it.
// Expand/collapse state is remembered for the duration of editing (plain
// component state, not persisted to the draft — collapsing a section
// isn't meaningful data to save).
function CollapsibleSection({ title, expanded, onToggle, children }) {
  return (
    <CollapsibleWrap>
      <CollapsibleHeader type="button" onClick={onToggle} aria-expanded={expanded}>
        <CollapsibleChevron $expanded={expanded}>▼</CollapsibleChevron>
        <CollapsibleTitle>{title}</CollapsibleTitle>
      </CollapsibleHeader>
      {expanded && <CollapsibleBody>{children}</CollapsibleBody>}
    </CollapsibleWrap>
  );
}

function ChipSelect({ options, selected, onChange, max = 0 }) {
  const toggle = (slug) => {
    const isSelected = selected.includes(slug);
    if (!isSelected && max > 0 && selected.length >= max) return;
    onChange(isSelected ? selected.filter((s) => s !== slug) : [...selected, slug]);
  };

  return (
    <ChipRow>
      {options.map((opt) => {
        const isSelected = selected.includes(opt.slug);
        const disabled = !isSelected && max > 0 && selected.length >= max;
        return (
          <Chip
            key={opt.slug}
            type="button"
            $selected={isSelected}
            $disabled={disabled}
            disabled={disabled}
            onClick={() => toggle(opt.slug)}
          >
            {opt.label}
          </Chip>
        );
      })}
    </ChipRow>
  );
}

function labelsFor(options, slugs) {
  return options
    .filter((o) => slugs.includes(o.slug))
    .map((o) => o.label);
}

// ---------- Question accordion (one field, collapsed-with-summary or
// expanded-with-picker). Not a lock: clicking a completed question's
// header reopens it for editing, same as any other question. ----------

const QuestionRow = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  background: none;
  border: none;
  border-top: 1px solid #241b3d;
  padding: 14px 0;
  cursor: pointer;
  text-align: left;

  &:hover {
    opacity: 0.8;
  }
`;

const QuestionHeaderLeft = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;
  min-width: 0;
  flex: 1;
`;

const QuestionCheck = styled.span`
  font-size: 14px;
  color: ${(props) => (props.$complete ? "#00f0b0" : "#4a3f6b")};
  flex-shrink: 0;
`;

const QuestionTitleText = styled.span`
  font-size: 15px;
  font-weight: 600;
  color: #f4f1fb;
  flex-shrink: 0;
`;

const QuestionSummaryText = styled.span`
  font-size: 13.5px;
  color: #9c90bc;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`;

const QuestionChevron = styled.span`
  font-size: 11px;
  color: #6c6188;
  flex-shrink: 0;
  transform: rotate(${(props) => (props.$open ? "0deg" : "-90deg")});
  transition: transform 0.15s;
`;

const QuestionBody = styled.div`
  padding: 2px 0 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

// The checkmark means "the required fields here are filled in," not
// "locked." Every header — complete or not — stays clickable, and
// clicking one simply reopens that question without touching any data.
function QuestionGroup({ title, isOpen, isComplete, summary, onToggle, error, children }) {
  return (
    <div>
      <QuestionRow type="button" onClick={onToggle} aria-expanded={isOpen}>
        <QuestionHeaderLeft>
          <QuestionCheck $complete={isComplete}>{isComplete ? "✓" : "○"}</QuestionCheck>
          <QuestionTitleText>{title}</QuestionTitleText>
          {!isOpen && <QuestionSummaryText>{summary}</QuestionSummaryText>}
        </QuestionHeaderLeft>
        <QuestionChevron $open={isOpen}>▼</QuestionChevron>
      </QuestionRow>
      {isOpen && (
        <QuestionBody>
          {children}
          {error && <ErrorText>{error}</ErrorText>}
        </QuestionBody>
      )}
    </div>
  );
}

// ---------- Search-first chip picker for long option lists (Format,
// Themes, Platforms-without-grouping, Languages). Shows a search box, a
// short "Popular" default set, and a "Show all" reveal — instead of
// dumping every option on screen at once. ----------

const SearchInput = styled.input`
  background: #0f0a1c;
  border: 1px solid #2c2245;
  border-radius: 8px;
  color: #f4f1fb;
  font-size: 14px;
  padding: 9px 12px;

  &:focus {
    outline: none;
    border-color: #00f0b0;
  }
`;

const ChipGroupLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #6c6188;
`;

const ShowMoreButton = styled.button`
  align-self: flex-start;
  font-size: 13px;
  font-weight: 600;
  color: #00f0b0;
  background: none;
  border: none;
  padding: 2px 0;
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }
`;

function SearchableChipSelect({ options, selected, onChange, max = 0, popularCount = 6 }) {
  const [query, setQuery] = useState("");
  const [showAll, setShowAll] = useState(false);

  const trimmed = query.trim().toLowerCase();
  const filtered = trimmed ? options.filter((o) => o.label.toLowerCase().includes(trimmed)) : null;
  const visible = filtered ?? (showAll ? options : options.slice(0, popularCount));
  const hiddenCount = options.length - popularCount;

  return (
    <>
      <SearchInput
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={`Search ${options.length} options...`}
      />
      {!trimmed && <ChipGroupLabel>{showAll ? "All options" : "Popular"}</ChipGroupLabel>}
      {filtered && filtered.length === 0 ? (
        <HelpText>No matches.</HelpText>
      ) : (
        <ChipSelect options={visible} selected={selected} onChange={onChange} max={max} />
      )}
      {!trimmed && !showAll && hiddenCount > 0 && (
        <ShowMoreButton type="button" onClick={() => setShowAll(true)}>
          Show all ({options.length})
        </ShowMoreButton>
      )}
    </>
  );
}

// ---------- Platforms grouped by category instead of one 13-chip wall.
// Same flat `selected` array and `onChange` as ChipSelect — this is only
// a display grouping. ----------

function GroupedPlatformSelect({ selected, onChange }) {
  return (
    <>
      {PLATFORM_GROUPS.map((group) => (
        <div key={group.label}>
          <ChipGroupLabel>{group.label}</ChipGroupLabel>
          <ChipSelect
            options={PLATFORM_OPTIONS.filter((o) => group.slugs.includes(o.slug))}
            selected={selected}
            onChange={onChange}
          />
        </div>
      ))}
    </>
  );
}

// ---------------------------------------------------------------------------
// Role selection — first screen after login
// ---------------------------------------------------------------------------

function RoleSelect({ onSelect }) {
  return (
    <RoleSelectStage>
      <RoleSelectWrap>
        <RoleSelectIntro>
          <RoleSelectTitle>Choose the option that best describes you</RoleSelectTitle>
          <RoleSelectSubtitle>
            Share something you love with the RealityCheck community. Once approved, it&apos;ll appear on the site.
          </RoleSelectSubtitle>
        </RoleSelectIntro>
        <RoleSelectGrid>
          <RoleSelectCard type="button" onClick={() => onSelect("user")}>
            <RoleSelectEmoji>👤</RoleSelectEmoji>
            <RoleSelectLabel>I&apos;m a User</RoleSelectLabel>
          </RoleSelectCard>
          <RoleSelectCard type="button" onClick={() => onSelect("creator")}>
            <RoleSelectEmoji>🎨</RoleSelectEmoji>
            <RoleSelectLabel>I&apos;m a Creator</RoleSelectLabel>
          </RoleSelectCard>
        </RoleSelectGrid>
      </RoleSelectWrap>
    </RoleSelectStage>
  );
}

// ---------------------------------------------------------------------------
// Draft choice — shown after picking a role, only when a saved draft for
// that specific role exists. Creator and user drafts never mix: each role
// only ever sees its own draft, never the other's.
// ---------------------------------------------------------------------------

function DraftChoiceScreen({ roleLabel, draftInfo, onContinue, onStartNew }) {
  return (
    <Wrap>
      <StepHeader>
        <StepTitle>Continue where you left off?</StepTitle>
        <HelpText>
          We found a saved {roleLabel} draft. Last edited: {draftInfo.lastEditedText}.
        </HelpText>
      </StepHeader>
      <RoleGrid>
        <RoleCard type="button" onClick={onContinue}>
          <RoleEmoji>📝</RoleEmoji>
          <RoleTitle>Continue {roleLabel} Draft</RoleTitle>
        </RoleCard>
        <RoleCard type="button" onClick={onStartNew}>
          <RoleEmoji>✨</RoleEmoji>
          <RoleTitle>Start New {roleLabel} Submission</RoleTitle>
        </RoleCard>
      </RoleGrid>
    </Wrap>
  );
}

// ---------------------------------------------------------------------------
// Lightweight user submission
// ---------------------------------------------------------------------------

function UserSubmissionForm({ onBack, onSubmitted, currentUser, initialDraft }) {
  const ud = initialDraft || {};
  const [name, setName] = useState(ud.experienceName ?? "");
  const [url, setUrl] = useState(ud.url ?? "");
  const [notes, setNotes] = useState(ud.notes ?? "");
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  // Autosave — separate collection from creator drafts (user_drafts vs
  // creator_drafts), same debounce pattern. Keeping the two entirely
  // separate is deliberate: a user draft should never be mistakable for
  // a creator draft, or vice versa.
  const autosaveTimeoutRef = useRef(null);
  useEffect(() => {
    if (!currentUser?.uid) return;
    if (autosaveTimeoutRef.current) clearTimeout(autosaveTimeoutRef.current);
    autosaveTimeoutRef.current = setTimeout(() => {
      setDoc(
        doc(db, "user_drafts", currentUser.uid),
        {
          experienceName: name,
          url,
          notes,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      ).catch((err) => console.error("User draft autosave failed", err));
    }, 1500);
    return () => clearTimeout(autosaveTimeoutRef.current);
  }, [currentUser?.uid, name, url, notes]);

  const validate = () => {
    const next = {};
    if (!name.trim()) next.name = "Experience name is required.";
    if (!url.trim()) next.url = "A URL is required.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    const author = signedInAuthor();
    if (!author) {
      setErrors((prev) => ({ ...prev, submit: "Please sign in before submitting." }));
      return;
    }
    setSubmitting(true);
    try {
      await addDoc(collection(db, "submissions"), withoutUndefined({
        type: "user",
        schemaVersion: SCHEMA_VERSION,
        submittedByUid: author.uid,
        submittedByDisplayName: author.displayName,
        submittedByEmail: author.email,
        submittedByProfilePhoto: author.photoURL,
        experienceName: name.trim(),
        url: url.trim(),
        notes: notes.trim() || null,
        status: "pending_review",
        createdAt: serverTimestamp(),
      }));

      deleteDoc(doc(db, "user_drafts", author.uid)).catch((err) =>
        console.error("Could not clear user draft after submission", err)
      );

      onSubmitted();
    } catch (err) {
      console.error("Submission failed", err);
      setErrors((prev) => ({ ...prev, submit: submissionErrorMessage(err) }));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Wrap as="form" onSubmit={handleSubmit}>
      <StepHeader>
        <StepTitle>Share an experience</StepTitle>
        <HelpText>
          We'll review this and reach out to the creator if we need more information.
        </HelpText>
      </StepHeader>

      <Field>
        <Label>
          Experience Name<Required>Required</Required>
        </Label>
        <TextInput value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Pearl" />
        {errors.name && <ErrorText>{errors.name}</ErrorText>}
      </Field>

      <Field>
        <Label>
          Experience URL<Required>Required</Required>
        </Label>
        <TextInput value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://..." />
        {errors.url && <ErrorText>{errors.url}</ErrorText>}
      </Field>

      <Field>
        <Label>
          Notes<OptionalTag>Optional, but recommended</OptionalTag>
        </Label>
        <TextArea $short value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={NOTES_PLACEHOLDER} />
      </Field>

      {errors.submit && <ErrorText>{errors.submit}</ErrorText>}

      <NavRow>
        <SecondaryButton type="button" onClick={onBack}>
          Back
        </SecondaryButton>
        <PrimaryButton type="submit" disabled={submitting}>
          {submitting ? "Submitting..." : "Submit"}
        </PrimaryButton>
      </NavRow>
    </Wrap>
  );
}

// ---------------------------------------------------------------------------
// Creator submission — step-based wizard
// ---------------------------------------------------------------------------

function CreatorWizard({ onBack, onSubmitted, currentUser, initialDraft }) {
  // Resuming a saved draft: File objects (heroFile, referenceImages) can't
  // be persisted to Firestore, so those always start empty even on
  // resume — everything else (text, arrays, and step position) restores.
  const d = initialDraft || {};

  const [step, setStep] = useState(d.step ?? 0); // index into CREATOR_STEPS

  const [title, setTitle] = useState(d.title ?? "");
  // Public-facing, per-experience. One logged-in user may submit under
  // multiple studio/creator names, so this is plain text, not a profile
  // reference. Prefilled below from the user's most recent creator
  // submission (or their account display name) but always freely editable.
  const [creatorDisplayName, setCreatorDisplayName] = useState(
    d.creatorDisplayName ?? currentUser?.displayName ?? ""
  );
  const [primaryExperienceLink, setPrimaryExperienceLink] = useState(d.primaryExperienceLink ?? "");

  const [marketingDescription, setMarketingDescription] = useState(d.marketingDescription ?? "");
  const [experienceDescription, setExperienceDescription] = useState(d.experienceDescription ?? "");
  const [preview, setPreview] = useState(d.preview ?? "");

  const [heroFile, setHeroFile] = useState(null);
  const [heroPreviewUrl, setHeroPreviewUrl] = useState(null);
  const [heroIsLandscape, setHeroIsLandscape] = useState(true);
  useEffect(() => {
    if (!heroFile) {
      setHeroPreviewUrl(null);
      setHeroIsLandscape(true);
      return;
    }
    const url = URL.createObjectURL(heroFile);
    setHeroPreviewUrl(url);

    // Landscape check is advisory only — never blocks submission, just
    // shows a friendly heads-up so people aren't surprised by a crop later.
    const img = new Image();
    img.onload = () => {
      setHeroIsLandscape(img.naturalWidth >= img.naturalHeight);
    };
    img.src = url;

    // Revoke on cleanup so swapping images (or leaving the wizard) doesn't
    // leak object URLs — createObjectURL memory isn't freed automatically.
    return () => URL.revokeObjectURL(url);
  }, [heroFile]);

  // Optional guidance for the hero image AI prompt — not required, purely
  // to help creators get a closer first result out of ChatGPT.
  const [referenceImages, setReferenceImages] = useState([]);
  const [referenceImageUrls, setReferenceImageUrls] = useState([]);
  useEffect(() => {
    if (referenceImages.length === 0) {
      setReferenceImageUrls([]);
      return;
    }
    const urls = referenceImages.map((file) => URL.createObjectURL(file));
    setReferenceImageUrls(urls);
    return () => urls.forEach((url) => URL.revokeObjectURL(url));
  }, [referenceImages]);
  const [colorPalette, setColorPalette] = useState(d.colorPalette ?? "");
  const [imageStyles, setImageStyles] = useState(d.imageStyles ?? []);
  const [visualNotes, setVisualNotes] = useState(d.visualNotes ?? "");

  // Drag & drop + paste support for both image dropzones. Click-to-upload
  // still works via the hidden <input> — these are additive.
  const [heroDragging, setHeroDragging] = useState(false);
  const [refDragging, setRefDragging] = useState(false);

  const handleHeroDragOver = (e) => {
    e.preventDefault();
    setHeroDragging(true);
  };
  const handleHeroDragLeave = () => setHeroDragging(false);
  const handleHeroDrop = (e) => {
    e.preventDefault();
    setHeroDragging(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) setHeroFile(file);
  };

  const handleRefDragOver = (e) => {
    e.preventDefault();
    setRefDragging(true);
  };
  const handleRefDragLeave = () => setRefDragging(false);
  const handleRefDrop = (e) => {
    e.preventDefault();
    setRefDragging(false);
    const files = Array.from(e.dataTransfer?.files || []);
    if (files.length > 0) setReferenceImages(files);
  };

  // Ctrl/Cmd+V paste support — scoped to the Media step so pasting
  // elsewhere in the wizard doesn't unexpectedly grab an image. Pastes
  // always target the Primary Image (the far more common paste use case
  // than reference images).
  useEffect(() => {
    if (step !== 2) return;
    const handlePaste = (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      for (const item of items) {
        if (item.type.startsWith("image/")) {
          const file = item.getAsFile();
          if (file) {
            setHeroFile(file);
            e.preventDefault();
          }
          break;
        }
      }
    };
    window.addEventListener("paste", handlePaste);
    return () => window.removeEventListener("paste", handlePaste);
  }, [step]);

  const [formats, setFormats] = useState(d.formats ?? []);
  const [themes, setThemes] = useState(d.themes ?? []);
  const [accessMethods, setAccessMethods] = useState(d.accessMethods ?? []);
  const [locationModels, setLocationModels] = useState(d.locationModels ?? []);
  const [platforms, setPlatforms] = useState(d.platforms ?? []);
  const [platformNotes, setPlatformNotes] = useState(d.platformNotes ?? "");
  const [players, setPlayers] = useState(d.players ?? []);
  const [duration, setDuration] = useState(d.duration ?? []);

  const [pricingModels, setPricingModels] = useState(d.pricingModels ?? []);
  const [priceDetails, setPriceDetails] = useState(d.priceDetails ?? "");
  const [availability, setAvailability] = useState(d.availability ?? []);
  const [availabilityDetails, setAvailabilityDetails] = useState(d.availabilityDetails ?? "");

  // Structured location — only collected when locationModels includes Fixed
  // or Multiple Locations (see requiresLocation below). "Anywhere"
  // experiences (Pokémon GO-style) never need an address, and a hybrid
  // experience (headset + venue) can be both anywhere AND fixed-location.
  // Structured location fields (venueName, streetAddress, city,
  // stateRegion, country, mapsLink) are no longer collected as live
  // component state — they're derived fresh from locationText via
  // parseLocationText() at submit time instead (see handleFinalSubmit).
  // Keeping them as unused useState here would just be dead bindings.

  // The actual creator-facing location field — one pasteable block instead
  // of five separate inputs, since creators usually already have this
  // exact text sitting on their website or ticketing page.
  const [locationText, setLocationText] = useState(d.locationText ?? "");

  const [recommendedAudiences, setRecommendedAudiences] = useState(d.recommendedAudiences ?? []);
  const [supportedLanguages, setSupportedLanguages] = useState(d.supportedLanguages ?? []);
  const [contentNotes, setContentNotes] = useState(d.contentNotes ?? "");
  const [creatorNotes, setCreatorNotes] = useState(d.creatorNotes ?? "");

  // Prefill Creator, Studio, or Organization from the user's most recent creator submission,
  // if they have one — falls back to their account display name otherwise.
  // Re-runs if currentUser resolves asynchronously after mount (common with
  // Firebase auth listeners), not just once — but never overwrites a value
  // the creator has already typed.
  useEffect(() => {
    if (!currentUser?.uid) return;
    let cancelled = false;

    (async () => {
      try {
        const q = query(
          collection(db, "submissions"),
          where("submittedByUid", "==", currentUser.uid),
          where("type", "==", "creator"),
          orderBy("createdAt", "desc"),
          limit(1)
        );
        const snap = await getDocs(q);
        if (cancelled) return;

        const lastCreatorName = !snap.empty ? snap.docs[0].data()?.creatorDisplayName : null;
        const fallback = lastCreatorName || currentUser.displayName || "";
        if (fallback) {
          // Functional update so this never stomps on something the
          // creator already typed while the query was in flight.
          setCreatorDisplayName((current) => current || fallback);
        }
      } catch (err) {
        // Non-critical — if this fails, the field just falls back to
        // whatever it was initialized with (display name or blank).
        console.error("Could not prefill creator display name", err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [currentUser?.uid, currentUser?.displayName]);

  // Only Fixed Location and Multiple Locations need a physical address.
  // "Anywhere" (Pokémon GO-style) and remote-only experiences don't — and
  // an experience can be both (e.g. a headset title that's also playable
  // at a fixed venue), so this checks membership, not exclusivity.
  const requiresLocation =
    locationModels.includes("fixed-location") || locationModels.includes("multiple-locations");

  // Autosave the draft (debounced ~1.5s after the last change) so a
  // creator can close the tab and pick up later via "Continue Draft" on
  // the role screen. Skipped entirely for logged-out users — drafts are
  // per-uid. File objects (heroFile, referenceImages) are NOT persisted;
  // Firestore can't store binary File data, so a resumed draft always
  // needs the images re-attached. That's an inherent limitation of this
  // approach, not an oversight — flagging it here so it isn't "fixed" by
  // trying to base64-encode images into Firestore docs instead, which
  // would blow past document size limits fast.
  const autosaveTimeoutRef = useRef(null);
  useEffect(() => {
    if (!currentUser?.uid) return;

    if (autosaveTimeoutRef.current) clearTimeout(autosaveTimeoutRef.current);
    autosaveTimeoutRef.current = setTimeout(() => {
      setDoc(
        doc(db, "creator_drafts", currentUser.uid),
        {
          step,
          title,
          creatorDisplayName,
          primaryExperienceLink,
          marketingDescription,
          experienceDescription,
          preview,
          colorPalette,
          imageStyles,
          visualNotes,
          formats,
          themes,
          accessMethods,
          locationModels,
          platforms,
          platformNotes,
          players,
          duration,
          pricingModels,
          priceDetails,
          availability,
          availabilityDetails,
          locationText,
          recommendedAudiences,
          supportedLanguages,
          contentNotes,
          creatorNotes,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      ).catch((err) => console.error("Draft autosave failed", err));
    }, 1500);

    return () => clearTimeout(autosaveTimeoutRef.current);
    // Intentionally depends on every persisted field so any edit schedules
    // a save — this is the one place in the file where an exhaustive
    // dependency list is actually the correct choice, not a lint chore.
  }, [
    currentUser?.uid,
    step,
    title,
    creatorDisplayName,
    primaryExperienceLink,
    marketingDescription,
    experienceDescription,
    preview,
    colorPalette,
    imageStyles,
    visualNotes,
    formats,
    themes,
    accessMethods,
    locationModels,
    platforms,
    platformNotes,
    players,
    duration,
    pricingModels,
    priceDetails,
    availability,
    availabilityDetails,
    locationText,
    recommendedAudiences,
    supportedLanguages,
    contentNotes,
    creatorNotes,
  ]);

  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  // Collapsible groups within the merged Experience Information step —
  // collapsed by default, remembered for the duration of editing.
  const [expandedSections, setExpandedSections] = useState({
    experience: false,
    access: false,
    availability: false,
    audience: false,
    location: false,
    additional: false,
  });
  const toggleSection = (key) =>
    setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));

  // Whether a given question already has an answer. Optional fields are
  // always "complete" — there's nothing to force. This is presentation
  // logic only; it doesn't change what's actually required to submit
  // (see getStepErrors below, which is the real source of truth).
  const isFieldComplete = (key) => {
    switch (key) {
      case "format":
        return formats.length > 0;
      case "themes":
        return themes.length > 0;
      case "players":
        return players.length > 0;
      case "duration":
        return duration.length > 0;
      case "accessMethod":
        return accessMethods.length > 0;
      case "locationModel":
        return locationModels.length > 0;
      case "platforms":
        return platforms.length > 0;
      case "platformNotes":
        return true;
      case "pricingModel":
        return pricingModels.length > 0;
      case "priceDetails":
        return priceDetails.trim() !== "";
      case "availability":
        return availability.length > 0;
      case "availabilityDetails":
        return true;
      case "recommendedAudience":
        return recommendedAudiences.length > 0;
      case "supportedLanguages":
        return supportedLanguages.length > 0;
      case "primaryLocation":
        return !requiresLocation || locationText.trim() !== "";
      case "contentNotes":
      case "creatorNotes":
        return true;
      default:
        return false;
    }
  };

  // Short collapsed-row summary per question — selected values if any,
  // otherwise a light hint of what's expected. This is what replaces
  // "0/5 selected" style counters with plain language.
  const fieldSummary = (key) => {
    switch (key) {
      case "format":
        return formats.length ? labelsFor(FORMAT_OPTIONS, formats).join(" • ") : "Choose up to 3";
      case "themes":
        return themes.length ? labelsFor(THEME_OPTIONS, themes).join(" • ") : "Choose up to 5";
      case "players":
        return players.length ? labelsFor(PLAYER_OPTIONS, players).join(" • ") : "Choose at least 1";
      case "duration":
        return duration.length ? labelsFor(DURATION_OPTIONS, duration).join(" • ") : "Choose at least 1";
      case "accessMethod":
        return accessMethods.length ? labelsFor(ACCESS_OPTIONS, accessMethods).join(" • ") : "Choose at least 1";
      case "locationModel":
        return locationModels.length
          ? labelsFor(LOCATION_MODEL_OPTIONS, locationModels).join(" • ")
          : "Choose at least 1";
      case "platforms":
        return platforms.length ? labelsFor(PLATFORM_OPTIONS, platforms).join(" • ") : "Choose at least 1";
      case "platformNotes":
        return platformNotes || "Optional";
      case "pricingModel":
        return pricingModels.length
          ? labelsFor(PRICING_MODEL_OPTIONS, pricingModels).join(" • ")
          : "Choose at least 1";
      case "priceDetails":
        return priceDetails || "Required";
      case "availability":
        return availability.length ? labelsFor(AVAILABILITY_OPTIONS, availability).join(" • ") : "Choose at least 1";
      case "availabilityDetails":
        return availabilityDetails || "Optional";
      case "recommendedAudience":
        return recommendedAudiences.length
          ? labelsFor(RECOMMENDED_AUDIENCE_OPTIONS, recommendedAudiences).join(" • ")
          : "Choose at least 1";
      case "supportedLanguages":
        return supportedLanguages.length
          ? labelsFor(SUPPORTED_LANGUAGE_OPTIONS, supportedLanguages).join(" • ")
          : "Choose at least 1";
      case "primaryLocation":
        return locationText ? locationText.split("\n")[0] : "Required";
      case "contentNotes":
        return contentNotes || "Optional";
      case "creatorNotes":
        return creatorNotes || "Optional";
      default:
        return "";
    }
  };

  // Which question is open per section — one at a time. Initialized to
  // the first INCOMPLETE question in each section, so resuming a draft
  // that already has, say, Format filled in opens Themes instead of
  // starting the user back at the beginning.
  const [openQuestions, setOpenQuestions] = useState(() => {
    const firstIncomplete = (fields) => fields.find((key) => !isFieldComplete(key)) ?? null;
    return {
      experience: firstIncomplete(SECTION_FIELD_ORDER.experience),
      access: firstIncomplete(SECTION_FIELD_ORDER.access),
      availability: firstIncomplete(SECTION_FIELD_ORDER.availability),
      audience: firstIncomplete(SECTION_FIELD_ORDER.audience),
      location: firstIncomplete(SECTION_FIELD_ORDER.location),
      additional: firstIncomplete(SECTION_FIELD_ORDER.additional),
    };
  });

  // Manual click on a question header — exclusive within its section
  // (opening one closes any other open question in the same section),
  // and toggles closed if it's already open. Never locked: this works
  // identically whether the question is complete or not.
  const toggleQuestion = (sectionKey, fieldKey) => {
    setOpenQuestions((prev) => ({
      ...prev,
      [sectionKey]: prev[sectionKey] === fieldKey ? null : fieldKey,
    }));
  };

  // Called after a question's value changes. If that question was the
  // open one and just became complete, auto-collapse it and open the
  // next incomplete question in the section — or close the section
  // entirely if everything's done. Does nothing if the question isn't
  // the currently-open one (e.g. a background autosave-triggered update).
  const advanceQuestion = (sectionKey, fieldKey, justCompleted) => {
    if (!justCompleted) return;
    setOpenQuestions((prev) => {
      if (prev[sectionKey] !== fieldKey) return prev;
      const order = SECTION_FIELD_ORDER[sectionKey];
      const next = order.slice(order.indexOf(fieldKey) + 1).find((k) => !isFieldComplete(k));
      return { ...prev, [sectionKey]: next ?? null };
    });
  };

  // After a failed validation on this step, auto-open (and expand the
  // parent section of) the first question that's actually blocking
  // submission, rather than leaving the user to hunt for it.
  useEffect(() => {
    if (step !== 3) return;
    const errorKeys = Object.keys(errors);
    if (errorKeys.length === 0) return;
    for (const [sectionKey, fields] of Object.entries(SECTION_FIELD_ORDER)) {
      for (const fieldKey of fields) {
        const errKey = ERROR_KEY_FOR_FIELD[fieldKey];
        if (errKey && errors[errKey]) {
          setExpandedSections((prev) => ({ ...prev, [sectionKey]: true }));
          setOpenQuestions((prev) => ({ ...prev, [sectionKey]: fieldKey }));
          return;
        }
      }
    }
  }, [errors, step]);

  // Nothing is required to submit. Empty fields are stored as empty
  // strings, empty arrays, or null and can be filled in later.
  const getStepErrors = () => ({});

  const validateAllStepsForSubmit = () => {
    for (let i = 0; i < 4; i++) {
      const stepErrors = getStepErrors(i);
      if (Object.keys(stepErrors).length > 0) {
        setErrors(stepErrors);
        setStep(i);
        return false;
      }
    }
    setErrors({});
    return true;
  };

  const goNext = () => {
    setErrors({});
    setStep((s) => Math.min(s + 1, CREATOR_STEPS.length - 1));
  };

  const goBack = () => {
    if (step === 0) {
      onBack();
      return;
    }
    setErrors({});
    setStep((s) => s - 1);
  };

  // Free navigation — validation only runs at submit time.
  const goToStep = (target) => {
    if (target === step) return;
    setErrors({});
    setStep(target);
  };

  // Pre-select Format/Themes/Access/Location Model/Platforms from the
  // description text — see the keyword-matching helpers and comment near
  // FORMAT_KEYWORDS above for how this works and why it's deliberately not
  // real AI. Runs once, the first time Experience Details (step index 3)
  // is reached, however the creator got there (Next, a progress-bar click,
  // or a review-card edit link) — and only adds chips, never removes or
  // overrides ones the creator already picked. `chipsManuallyEditedRef` is
  // an explicit belt-and-suspenders guard on top of the one-shot ref below:
  // even if this effect somehow re-ran (StrictMode double-invoke, fast
  // refresh, etc.), it still could never overwrite a chip the creator has
  // touched by hand.
  // If resuming a draft that already has any of these five fields
  // populated, treat that as equivalent to a manual edit — never let the
  // suggestion heuristic run again and silently change a draft the
  // creator already left in a particular state.
  const draftHasClassification =
    (d.formats?.length ?? 0) > 0 ||
    (d.themes?.length ?? 0) > 0 ||
    (d.accessMethods?.length ?? 0) > 0 ||
    (d.locationModels?.length ?? 0) > 0 ||
    (d.platforms?.length ?? 0) > 0;
  const suggestionsAppliedRef = useRef(draftHasClassification);
  const chipsManuallyEditedRef = useRef(draftHasClassification);
  const [showSuggestionBanner, setShowSuggestionBanner] = useState(false);

  const handleFormatsChange = (next) => {
    chipsManuallyEditedRef.current = true;
    setFormats(next);
    advanceQuestion("experience", "format", next.length > 0);
  };
  const handleThemesChange = (next) => {
    chipsManuallyEditedRef.current = true;
    setThemes(next);
    advanceQuestion("experience", "themes", next.length > 0);
  };
  const handleAccessMethodsChange = (next) => {
    chipsManuallyEditedRef.current = true;
    setAccessMethods(next);
    advanceQuestion("access", "accessMethod", next.length > 0);
  };
  const handleLocationModelsChange = (next) => {
    chipsManuallyEditedRef.current = true;
    setLocationModels(next);
    advanceQuestion("access", "locationModel", next.length > 0);
  };
  const handlePlatformsChange = (next) => {
    chipsManuallyEditedRef.current = true;
    setPlatforms(next);
    advanceQuestion("access", "platforms", next.length > 0);
  };
  const handlePlayersChange = (next) => {
    setPlayers(next);
    advanceQuestion("experience", "players", next.length > 0);
  };
  const handleDurationChange = (next) => {
    setDuration(next);
    advanceQuestion("experience", "duration", next.length > 0);
  };
  const handlePricingModelsChange = (next) => {
    setPricingModels(next);
    advanceQuestion("availability", "pricingModel", next.length > 0);
  };
  const handleAvailabilityChange = (next) => {
    setAvailability(next);
    advanceQuestion("availability", "availability", next.length > 0);
  };
  const handleRecommendedAudiencesChange = (next) => {
    setRecommendedAudiences(next);
    advanceQuestion("audience", "recommendedAudience", next.length > 0);
  };
  const handleSupportedLanguagesChange = (next) => {
    setSupportedLanguages(next);
    advanceQuestion("audience", "supportedLanguages", next.length > 0);
  };
  // Text fields advance on blur rather than every keystroke — advancing
  // mid-typing would collapse the field out from under the user.
  const handlePlatformNotesBlur = () => advanceQuestion("access", "platformNotes", true);
  const handlePriceDetailsBlur = () =>
    advanceQuestion("availability", "priceDetails", priceDetails.trim() !== "");
  const handleAvailabilityDetailsBlur = () => advanceQuestion("availability", "availabilityDetails", true);
  const handleLocationTextBlur = () =>
    advanceQuestion("location", "primaryLocation", !requiresLocation || locationText.trim() !== "");
  const handleContentNotesBlur = () => advanceQuestion("additional", "contentNotes", true);
  const handleCreatorNotesBlur = () => advanceQuestion("additional", "creatorNotes", true);

  useEffect(() => {
    if (step !== 3 || suggestionsAppliedRef.current) return;
    suggestionsAppliedRef.current = true;

    // Belt-and-suspenders: bail if the creator has already touched any of
    // these chips by hand, even though normal effect timing makes this
    // path unreachable in practice (the creator can't click a chip on a
    // step before this effect — which runs on that step's first render —
    // has already fired).
    if (chipsManuallyEditedRef.current) return;

    const text = `${marketingDescription} ${experienceDescription}`;
    if (!text.trim()) return;

    let appliedAny = false;

    const mergeAdditive = (setter, keywordMap, max = 0) => {
      setter((prev) => {
        if (max > 0 && prev.length >= max) return prev;
        const suggested = suggestSlugsFromText(text, keywordMap);
        const merged = [...prev];
        for (const slug of suggested) {
          if (max > 0 && merged.length >= max) break;
          if (!merged.includes(slug)) {
            merged.push(slug);
            appliedAny = true;
          }
        }
        return merged;
      });
    };

    mergeAdditive(setFormats, FORMAT_KEYWORDS, FORMAT_MAX);
    mergeAdditive(setThemes, THEME_KEYWORDS, THEME_MAX);
    mergeAdditive(setAccessMethods, ACCESS_KEYWORDS);
    mergeAdditive(setLocationModels, LOCATION_MODEL_KEYWORDS);
    mergeAdditive(setPlatforms, PLATFORM_KEYWORDS);

    if (appliedAny) setShowSuggestionBanner(true);
  }, [step, marketingDescription, experienceDescription]);

  const uploadHeroImage = async (file, submissionId, uploaderUid) => {
    if (!file) return null;
    const safeName = String(file.name || "image").replace(/[^a-zA-Z0-9._-]/g, "-");
    const heroStoragePath = `submissions/hero-images/${submissionId}-${safeName}`;
    const storageRef = ref(storage, heroStoragePath);
    await uploadBytes(storageRef, file, { customMetadata: { submissionId, uploaderUid } });
    const url = await getDownloadURL(storageRef);
    return { url, storageRef, heroStoragePath };
  };

  const handleFinalSubmit = async () => {
    if (!validateAllStepsForSubmit()) {
      return;
    }
    const author = signedInAuthor();
    if (!author) {
      setErrors((prev) => ({
        ...prev,
        submit: "Please sign in before submitting. Nothing was saved.",
      }));
      return;
    }
    setSubmitting(true);
    const submissionRef = doc(collection(db, "submissions"));
    let uploadedHero = null;
    try {
      uploadedHero = await uploadHeroImage(heroFile, submissionRef.id, author.uid);

      await setDoc(submissionRef, withoutUndefined({
        type: "creator",
        schemaVersion: SCHEMA_VERSION,
        submittedByUid: author.uid,
        submittedByDisplayName: author.displayName,
        submittedByEmail: author.email,
        submittedByProfilePhoto: author.photoURL,
        title: title.trim(),
        creatorDisplayName: creatorDisplayName.trim(),
        primaryExperienceLink: primaryExperienceLink.trim(),
        marketingDescription: marketingDescription.trim(),
        experienceDescription: experienceDescription.trim(),
        realityCheckPreview: preview.trim(),
        heroImageUrl: uploadedHero ? uploadedHero.url : null,
        heroStoragePath: uploadedHero ? uploadedHero.heroStoragePath : null,
        colorPalette: colorPalette.trim() || null,
        imageStyles,
        visualNotes: visualNotes.trim() || null,
        formats,
        themes,
        accessMethods,
        locationModels,
        platforms,
        platformNotes: platformNotes.trim() || null,
        players,
        duration,
        pricingModels,
        priceDetails: priceDetails.trim() || null,
        availability,
        availabilityDetails: availabilityDetails.trim() || null,
        location: requiresLocation
          ? {
              locationText: locationText.trim(),
              hasMultipleLocations: locationModels.includes("multiple-locations"),
              ...parseLocationText(locationText),
              mapsLink: null,
            }
          : null,
        recommendedAudiences,
        supportedLanguages,
        contentNotes: contentNotes.trim() || null,
        creatorNotes: creatorNotes.trim() || null,
        status: "pending_review",
        createdAt: serverTimestamp(),
      }));

      deleteDoc(doc(db, "creator_drafts", author.uid)).catch((err) =>
        console.error("Could not clear draft after submission", err)
      );

      onSubmitted();
    } catch (err) {
      console.error("Submission failed", err);
      if (uploadedHero?.storageRef) {
        deleteObject(uploadedHero.storageRef).catch((cleanupErr) =>
          console.error("Could not delete hero image after failed submission", cleanupErr)
        );
      }
      setErrors((prev) => ({ ...prev, submit: submissionErrorMessage(err) }));
    } finally {
      setSubmitting(false);
    }
  };

  const previewExperience = useMemo(
    () =>
      buildSubmissionPreviewExperience(
        {
          title,
          creatorDisplayName,
          heroPreviewUrl,
          preview,
          experienceDescription,
          primaryExperienceLink,
          formats,
          themes,
          accessMethods,
          locationModels,
          platforms,
          platformNotes,
          players,
          duration,
          pricingModels,
          priceDetails,
          availability,
          availabilityDetails,
          recommendedAudiences,
          supportedLanguages,
          contentNotes,
          locationText,
          requiresLocation,
          referenceImageUrls,
        },
        {
          FORMAT_OPTIONS,
          PLATFORM_OPTIONS,
          PLAYER_OPTIONS,
          DURATION_OPTIONS,
          RECOMMENDED_AUDIENCE_OPTIONS,
          SUPPORTED_LANGUAGE_OPTIONS,
          AVAILABILITY_OPTIONS,
        }
      ),
    [
      title,
      creatorDisplayName,
      heroPreviewUrl,
      preview,
      experienceDescription,
      primaryExperienceLink,
      formats,
      themes,
      accessMethods,
      locationModels,
      platforms,
      platformNotes,
      players,
      duration,
      pricingModels,
      priceDetails,
      availability,
      availabilityDetails,
      recommendedAudiences,
      supportedLanguages,
      contentNotes,
      locationText,
      requiresLocation,
      referenceImageUrls,
    ]
  );

  return (
    <Wrap $wide={step === 4}>
      <TopNavRow>
        <SecondaryButton type="button" onClick={goBack}>
          {step === 0 ? "Back" : "Previous Section"}
        </SecondaryButton>
      </TopNavRow>

      <ProgressBar steps={CREATOR_STEPS} currentIndex={step} onStepClick={goToStep} />

      <StepHeader>
        <StepTitle>{CREATOR_STEPS[step]}</StepTitle>
        {step === 0 && <HelpText>Takes about 5–8 minutes. You can edit any section before submitting.</HelpText>}
      </StepHeader>

      {step === 0 && (
        <>
          <Field>
            <Label>
              Experience Title
            </Label>
            <TextInput value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Pearl" />
            {errors.title && <ErrorText>{errors.title}</ErrorText>}
          </Field>

          <Field>
            <Label>
              Creator, Studio, or Organization
            </Label>
            <HelpText>
              This is per experience — if you publish under more than one name, studio, or
              organization, you can change it each time.
            </HelpText>
            <TextInput
              value={creatorDisplayName}
              onChange={(e) => setCreatorDisplayName(e.target.value)}
              placeholder="e.g. Lucid Theatre Co."
            />
            {errors.creatorDisplayName && <ErrorText>{errors.creatorDisplayName}</ErrorText>}
          </Field>

          <Field>
            <Label>
              Experience Link
            </Label>
            <HelpText>
              The best place to launch, purchase, reserve, or learn more — must already be live,
              public or unlisted. No file upload needed, just a link.
            </HelpText>
            <TextInput
              value={primaryExperienceLink}
              onChange={(e) => setPrimaryExperienceLink(e.target.value)}
              placeholder="https://..."
            />
            {errors.primaryExperienceLink && <ErrorText>{errors.primaryExperienceLink}</ErrorText>}
          </Field>
        </>
      )}

      {step === 1 && (
        <>
          <Field>
            <Label>
              Marketing Description
            </Label>
            <HelpText>
              Paste the description you already use on your website, ticketing page, app store
              listing, Steam page, Quest listing, or press kit.
            </HelpText>
            <TextArea
              value={marketingDescription}
              onChange={(e) => setMarketingDescription(e.target.value)}
              placeholder="From your website, ticket page, Steam page, Quest listing, press kit, App Store listing..."
            />
            {errors.marketingDescription && <ErrorText>{errors.marketingDescription}</ErrorText>}
          </Field>

          <PipelineArrow>Generate Experience Description</PipelineArrow>

          <Field>
            <Label>
              Experience Description
            </Label>
            <HelpText>
              This is what appears on your RealityCheck experience page. Focus on what participants
              actually experience.
            </HelpText>
            <TextArea
              value={experienceDescription}
              onChange={(e) => setExperienceDescription(e.target.value)}
              placeholder="Use the AI Helper below to generate your RealityCheck Experience Description, then paste it here. Review and edit it before continuing."
            />
            {errors.experienceDescription && <ErrorText>{errors.experienceDescription}</ErrorText>}

            <AiPromptHelper
              label="Experience Description"
              description="Copies a prompt with your Marketing Description already included."
              promptText={buildExperienceDescriptionPrompt(marketingDescription)}
            />
          </Field>

          <PipelineArrow>Generate Preview</PipelineArrow>

          <Field>
            <LabelRow>
              <Label>
                RealityCheck Preview
              </Label>
              <CharCount>
                {preview.length}/{PREVIEW_MAX_LENGTH}
              </CharCount>
            </LabelRow>
            <TextArea
              $short
              maxLength={PREVIEW_MAX_LENGTH}
              value={preview}
              onChange={(e) => setPreview(e.target.value)}
              placeholder="Use the AI Helper below to generate your RealityCheck Preview, then paste it here. Review and edit it before continuing."
            />
            {errors.preview && <ErrorText>{errors.preview}</ErrorText>}

            <AiPromptHelper
              label="RealityCheck Preview"
              description="Copies a prompt with your Experience Description already included."
              promptText={buildPreviewPrompt(experienceDescription)}
            />
          </Field>
        </>
      )}

      {step === 2 && (
        <>
          <Field>
            <Label>
              Primary Image
            </Label>
            <HelpText>
              Upload your primary image if you have one. Don't have one yet? Complete the optional
              visual details below first, then use the AI Helper at the bottom of this section to
              generate one.
            </HelpText>
            <Dropzone
              $filled={!!heroFile}
              $dragging={heroDragging}
              onDragOver={handleHeroDragOver}
              onDragLeave={handleHeroDragLeave}
              onDrop={handleHeroDrop}
            >
              <input type="file" accept="image/*" onChange={(e) => setHeroFile(e.target.files?.[0] || null)} />
              <DropzoneTitle>{heroFile ? heroFile.name : "Click to upload, drag and drop, or paste an image"}</DropzoneTitle>
              <DropzoneSub>Landscape image recommended (16:9 works best)</DropzoneSub>
            </Dropzone>
            {heroPreviewUrl && <HeroPreviewImg src={heroPreviewUrl} alt="" />}
            {heroFile && !heroIsLandscape && (
              <HelpText>
                Landscape images work best. You can still submit this, but it may be cropped.
              </HelpText>
            )}
            {errors.heroFile && <ErrorText>{errors.heroFile}</ErrorText>}
          </Field>

          <Field>
            <Label>
              Reference Images<OptionalTag>Optional</OptionalTag>
            </Label>
            <HelpText>
              Any existing screenshots, concept art, or photos you'd want an AI image generator to
              see. Attach these yourself when you paste the prompt into ChatGPT.
            </HelpText>
            <Dropzone
              $filled={referenceImages.length > 0}
              $dragging={refDragging}
              onDragOver={handleRefDragOver}
              onDragLeave={handleRefDragLeave}
              onDrop={handleRefDrop}
            >
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => setReferenceImages(Array.from(e.target.files || []))}
              />
              <DropzoneTitle>
                {referenceImages.length > 0
                  ? `${referenceImages.length} image${referenceImages.length > 1 ? "s" : ""} selected`
                  : "Click to upload, or drag and drop reference images"}
              </DropzoneTitle>
            </Dropzone>
          </Field>

          <Field>
            <Label>
              Preferred Color Palette<OptionalTag>Optional</OptionalTag>
            </Label>
            <TextInput
              value={colorPalette}
              onChange={(e) => setColorPalette(e.target.value)}
              placeholder="e.g. Dark neon blues, warm desert tones, pastel fantasy"
            />
          </Field>

          <Field>
            <Label>
              Preferred Image Style<OptionalTag>Optional</OptionalTag>
            </Label>
            <ChipSelect options={IMAGE_STYLE_OPTIONS} selected={imageStyles} onChange={setImageStyles} />
          </Field>

          <Field>
            <Label>
              Visual Notes<OptionalTag>Optional</OptionalTag>
            </Label>
            <TextArea
              $short
              value={visualNotes}
              onChange={(e) => setVisualNotes(e.target.value)}
              placeholder="Anything else an image generator should know about the look and feel."
            />
          </Field>

          <AiPromptHelper
            label="Primary Image"
            description="Copies a prompt with your Experience Description, preferred color palette, preferred image style, visual notes, and any uploaded reference images already incorporated."
            promptText={buildHeroImagePrompt(
              title,
              experienceDescription,
              colorPalette,
              visualNotes,
              labelsFor(IMAGE_STYLE_OPTIONS, imageStyles).join(", ")
            )}
          />
        </>
      )}

      {step === 3 && (
        <>
          {showSuggestionBanner && (
            <HelpText>
              We preselected a few options from your experience description. You can keep them,
              deselect anything that's wrong, or choose different options.
            </HelpText>
          )}

          <CollapsibleSection
            title="Experience"
            expanded={expandedSections.experience}
            onToggle={() => toggleSection("experience")}
          >
            <QuestionGroup
              title="Format"
              isOpen={openQuestions.experience === "format"}
              isComplete={isFieldComplete("format")}
              summary={fieldSummary("format")}
              onToggle={() => toggleQuestion("experience", "format")}
              error={errors.formats}
            >
              <HelpText>Choose up to 3.</HelpText>
              <SearchableChipSelect options={FORMAT_OPTIONS} selected={formats} onChange={handleFormatsChange} max={FORMAT_MAX} />
              <HelpText>
                None of these quite fit? Pick the closest match and explain in Creator Notes below.
              </HelpText>
            </QuestionGroup>

            <QuestionGroup
              title="Themes"
              isOpen={openQuestions.experience === "themes"}
              isComplete={isFieldComplete("themes")}
              summary={fieldSummary("themes")}
              onToggle={() => toggleQuestion("experience", "themes")}
              error={errors.themes}
            >
              <HelpText>What is it about? Choose up to 5.</HelpText>
              <SearchableChipSelect options={THEME_OPTIONS} selected={themes} onChange={handleThemesChange} max={THEME_MAX} />
            </QuestionGroup>

            <QuestionGroup
              title="Players"
              isOpen={openQuestions.experience === "players"}
              isComplete={isFieldComplete("players")}
              summary={fieldSummary("players")}
              onToggle={() => toggleQuestion("experience", "players")}
              error={errors.players}
            >
              <ChipSelect options={PLAYER_OPTIONS} selected={players} onChange={handlePlayersChange} />
            </QuestionGroup>

            <QuestionGroup
              title="Session Length"
              isOpen={openQuestions.experience === "duration"}
              isComplete={isFieldComplete("duration")}
              summary={fieldSummary("duration")}
              onToggle={() => toggleQuestion("experience", "duration")}
              error={errors.duration}
            >
              <ChipSelect options={DURATION_OPTIONS} selected={duration} onChange={handleDurationChange} />
            </QuestionGroup>
          </CollapsibleSection>

          <CollapsibleSection
            title="Access"
            expanded={expandedSections.access}
            onToggle={() => toggleSection("access")}
          >
            <QuestionGroup
              title="Access Method"
              isOpen={openQuestions.access === "accessMethod"}
              isComplete={isFieldComplete("accessMethod")}
              summary={fieldSummary("accessMethod")}
              onToggle={() => toggleQuestion("access", "accessMethod")}
              error={errors.accessMethods}
            >
              <ChipSelect options={ACCESS_OPTIONS} selected={accessMethods} onChange={handleAccessMethodsChange} />
              <HelpText>{CONTENT_PLATFORM_HELP_TEXT}</HelpText>
            </QuestionGroup>

            <QuestionGroup
              title="Where does this take place?"
              isOpen={openQuestions.access === "locationModel"}
              isComplete={isFieldComplete("locationModel")}
              summary={fieldSummary("locationModel")}
              onToggle={() => toggleQuestion("access", "locationModel")}
              error={errors.locationModels}
            >
              <HelpText>
                Location-Flexible / On the Go: many real-world places. At Home / Personal Space:
                private, on your own device.
              </HelpText>
              <ChipSelect options={LOCATION_MODEL_OPTIONS} selected={locationModels} onChange={handleLocationModelsChange} />
            </QuestionGroup>

            <QuestionGroup
              title="Compatible Platforms"
              isOpen={openQuestions.access === "platforms"}
              isComplete={isFieldComplete("platforms")}
              summary={fieldSummary("platforms")}
              onToggle={() => toggleQuestion("access", "platforms")}
              error={errors.platforms}
            >
              <GroupedPlatformSelect selected={platforms} onChange={handlePlatformsChange} />
            </QuestionGroup>

            <QuestionGroup
              title="Additional Compatibility Notes"
              isOpen={openQuestions.access === "platformNotes"}
              isComplete={isFieldComplete("platformNotes")}
              summary={fieldSummary("platformNotes")}
              onToggle={() => toggleQuestion("access", "platformNotes")}
            >
              <TextInput
                value={platformNotes}
                onChange={(e) => setPlatformNotes(e.target.value)}
                onBlur={handlePlatformNotesBlur}
                placeholder="e.g. Also works on HTC Vive."
              />
            </QuestionGroup>
          </CollapsibleSection>

          <CollapsibleSection
            title="Availability"
            expanded={expandedSections.availability}
            onToggle={() => toggleSection("availability")}
          >
            <QuestionGroup
              title="Pricing Model"
              isOpen={openQuestions.availability === "pricingModel"}
              isComplete={isFieldComplete("pricingModel")}
              summary={fieldSummary("pricingModel")}
              onToggle={() => toggleQuestion("availability", "pricingModel")}
              error={errors.pricingModels}
            >
              <ChipSelect options={PRICING_MODEL_OPTIONS} selected={pricingModels} onChange={handlePricingModelsChange} />
            </QuestionGroup>

            <QuestionGroup
              title="Price Details"
              isOpen={openQuestions.availability === "priceDetails"}
              isComplete={isFieldComplete("priceDetails")}
              summary={fieldSummary("priceDetails")}
              onToggle={() => toggleQuestion("availability", "priceDetails")}
              error={errors.priceDetails}
            >
              <TextInput
                value={priceDetails}
                onChange={(e) => setPriceDetails(e.target.value)}
                onBlur={handlePriceDetailsBlur}
                placeholder="e.g. $25, $40/person, Included with museum admission"
              />
            </QuestionGroup>

            <QuestionGroup
              title="Availability"
              isOpen={openQuestions.availability === "availability"}
              isComplete={isFieldComplete("availability")}
              summary={fieldSummary("availability")}
              onToggle={() => toggleQuestion("availability", "availability")}
              error={errors.availability}
            >
              <ChipSelect options={AVAILABILITY_OPTIONS} selected={availability} onChange={handleAvailabilityChange} />
            </QuestionGroup>

            <QuestionGroup
              title="Availability Details"
              isOpen={openQuestions.availability === "availabilityDetails"}
              isComplete={isFieldComplete("availabilityDetails")}
              summary={fieldSummary("availabilityDetails")}
              onToggle={() => toggleQuestion("availability", "availabilityDetails")}
            >
              <TextInput
                value={availabilityDetails}
                onChange={(e) => setAvailabilityDetails(e.target.value)}
                onBlur={handleAvailabilityDetailsBlur}
                placeholder="e.g. Available now in venue, headset version coming soon."
              />
            </QuestionGroup>
          </CollapsibleSection>

          <CollapsibleSection
            title="Audience"
            expanded={expandedSections.audience}
            onToggle={() => toggleSection("audience")}
          >
            <QuestionGroup
              title="Recommended Audience"
              isOpen={openQuestions.audience === "recommendedAudience"}
              isComplete={isFieldComplete("recommendedAudience")}
              summary={fieldSummary("recommendedAudience")}
              onToggle={() => toggleQuestion("audience", "recommendedAudience")}
              error={errors.recommendedAudiences}
            >
              <ChipSelect
                options={RECOMMENDED_AUDIENCE_OPTIONS}
                selected={recommendedAudiences}
                onChange={handleRecommendedAudiencesChange}
              />
            </QuestionGroup>

            <QuestionGroup
              title="Supported Languages"
              isOpen={openQuestions.audience === "supportedLanguages"}
              isComplete={isFieldComplete("supportedLanguages")}
              summary={fieldSummary("supportedLanguages")}
              onToggle={() => toggleQuestion("audience", "supportedLanguages")}
              error={errors.supportedLanguages}
            >
              <SearchableChipSelect
                options={SUPPORTED_LANGUAGE_OPTIONS}
                selected={supportedLanguages}
                onChange={handleSupportedLanguagesChange}
              />
            </QuestionGroup>
          </CollapsibleSection>

          {requiresLocation && (
            <CollapsibleSection
              title="Location"
              expanded={expandedSections.location}
              onToggle={() => toggleSection("location")}
            >
              <HelpText>Tell people where they can experience this.</HelpText>
              {locationModels.includes("multiple-locations") && (
                <AccentNote>
                  This experience appears in multiple locations. Additional locations can be added
                  after approval.
                </AccentNote>
              )}

              <QuestionGroup
                title="Primary Location"
                isOpen={openQuestions.location === "primaryLocation"}
                isComplete={isFieldComplete("primaryLocation")}
                summary={fieldSummary("primaryLocation")}
                onToggle={() => toggleQuestion("location", "primaryLocation")}
                error={errors.locationText}
              >
                <HelpText>This will be displayed on RealityCheck.</HelpText>
                <TextArea
                  value={locationText}
                  onChange={(e) => setLocationText(e.target.value)}
                  onBlur={handleLocationTextBlur}
                  placeholder={"Sandbox VR Santa Monica\n1453 3rd Street Promenade\nSanta Monica, CA 90401"}
                />
              </QuestionGroup>
            </CollapsibleSection>
          )}

          <CollapsibleSection
            title="Additional Information"
            expanded={expandedSections.additional}
            onToggle={() => toggleSection("additional")}
          >
            <QuestionGroup
              title="Content Notes"
              isOpen={openQuestions.additional === "contentNotes"}
              isComplete={isFieldComplete("contentNotes")}
              summary={fieldSummary("contentNotes")}
              onToggle={() => toggleQuestion("additional", "contentNotes")}
            >
              <HelpText>
                Anything people should know before diving in — flashing lights, jump scares,
                physical activity, and so on.
              </HelpText>
              <TextArea
                $short
                value={contentNotes}
                onChange={(e) => setContentNotes(e.target.value)}
                onBlur={handleContentNotesBlur}
                placeholder="e.g. Includes brief strobe effects and moderate physical movement."
              />
            </QuestionGroup>

            <QuestionGroup
              title="Creator Notes"
              isOpen={openQuestions.additional === "creatorNotes"}
              isComplete={isFieldComplete("creatorNotes")}
              summary={fieldSummary("creatorNotes")}
              onToggle={() => toggleQuestion("additional", "creatorNotes")}
            >
              <HelpText>Private — not shown publicly. Anything else the RealityCheck team should know?</HelpText>
              <TextArea
                $short
                value={creatorNotes}
                onChange={(e) => setCreatorNotes(e.target.value)}
                onBlur={handleCreatorNotesBlur}
                placeholder="e.g. The Quest version launches next month. Please use the Steam trailer instead of the website. This is part of a larger series."
              />
            </QuestionGroup>
          </CollapsibleSection>
        </>
      )}

      {step === 4 && (
        <>
          <HelpText>
            Here's how this will look to someone discovering it on RealityCheck.
          </HelpText>

          <SimpleModal previewMode experience={previewExperience} />

          <ReadyToSubmitCard>
            <ReadyToSubmitTitle>✓ Looks good?</ReadyToSubmitTitle>
            <HelpText>
              You'll be able to edit this after submission if our moderators request changes.
            </HelpText>
          </ReadyToSubmitCard>

          {errors.submit && <ErrorText>{errors.submit}</ErrorText>}
        </>
      )}

      <NavRow $end>
        {step < CREATOR_STEPS.length - 1 ? (
          <PrimaryButton type="button" onClick={goNext}>
            Next
          </PrimaryButton>
        ) : (
          <PrimaryButton
            type="button"
            onClick={handleFinalSubmit}
            disabled={submitting}
          >
            {submitting ? "Submitting..." : "Submit for Review"}
          </PrimaryButton>
        )}
      </NavRow>
    </Wrap>
  );
}

// ---------------------------------------------------------------------------
// Success screen
// ---------------------------------------------------------------------------

function SuccessScreen({ role, onDone, onSubmitAnother }) {
  return (
    <SuccessWrap>
      <SuccessTitle>Thanks for your submission!</SuccessTitle>
      <HelpText>
        {role === "creator"
          ? "Your experience has been submitted for review. We'll review it for quality and completeness before it appears on RealityCheck. If we have any questions, we'll reach out."
          : "Your recommendation has been submitted for review. We'll reach out to the creator if we need more information before it appears on RealityCheck."}
      </HelpText>
      <SuccessButtons>
        <SecondaryButton type="button" onClick={onDone}>
          Done
        </SecondaryButton>
        {role === "creator" && isCreatorSubmissionOpen() && (
          <PrimaryButton type="button" onClick={onSubmitAnother}>
            Submit Another Experience
          </PrimaryButton>
        )}
      </SuccessButtons>
    </SuccessWrap>
  );
}

// ---------------------------------------------------------------------------
// Top-level flow — mount this where the old submission form used to live
// ---------------------------------------------------------------------------

export default function ShareExperienceFlow({ onClose, currentUser, onRequestSignIn }) {
  const [phase, setPhase] = useState("role"); // 'role' | 'creatorDraftChoice' | 'userDraftChoice' | 'user' | 'creator' | 'success'
  const [completedRole, setCompletedRole] = useState(null);
  const [showCreatorComingSoon, setShowCreatorComingSoon] = useState(false);
  const [creatorDraftInfo, setCreatorDraftInfo] = useState(null); // { data, lastEditedText } | null
  const [userDraftInfo, setUserDraftInfo] = useState(null); // { data, lastEditedText } | null
  // Which draft (if any) to actually load into the form once the creator
  // makes a choice on the draft-choice screen — kept separate from
  // creatorDraftInfo/userDraftInfo so "Start New" can bypass the draft
  // without needing to delete it first (autosave will just overwrite it).
  const [creatorInitialDraft, setCreatorInitialDraft] = useState(null);
  const [userInitialDraft, setUserInitialDraft] = useState(null);

  const formatLastEdited = (data) => {
    const updatedAt = data.updatedAt?.toDate ? data.updatedAt.toDate() : null;
    return updatedAt
      ? updatedAt.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })
      : "recently";
  };

  // Re-check for existing drafts every time the role screen is shown, not
  // just once on mount. Mount-only checking missed the common case of:
  // start a submission -> autosave kicks in -> back out to the role
  // screen -> the just-created draft wouldn't show up as "Continue Draft"
  // because the check never re-ran. Also explicitly clears stale draft
  // state when a doc no longer exists (e.g. after "Start New" eventually
  // overwrites it, or a draft is deleted elsewhere).
  useEffect(() => {
    if (!currentUser?.uid || phase !== "role") return;
    let cancelled = false;
    (async () => {
      try {
        const [creatorSnap, userSnap] = await Promise.all([
          getDoc(doc(db, "creator_drafts", currentUser.uid)),
          getDoc(doc(db, "user_drafts", currentUser.uid)),
        ]);
        if (cancelled) return;
        if (creatorSnap.exists()) {
          const data = creatorSnap.data();
          setCreatorDraftInfo({ data, lastEditedText: formatLastEdited(data) });
        } else {
          setCreatorDraftInfo(null);
        }
        if (userSnap.exists()) {
          const data = userSnap.data();
          setUserDraftInfo({ data, lastEditedText: formatLastEdited(data) });
        } else {
          setUserDraftInfo(null);
        }
      } catch (err) {
        console.error("Could not check for existing drafts", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentUser?.uid, phase]);

  // The role screen itself never shows draft state — picking a role is
  // where the draft check happens, one level deeper.
  const handleRoleSelect = (role) => {
    if (role === "user" && !currentUser?.uid) {
      onRequestSignIn?.("signup");
      return;
    }
    if (role === "creator") {
      if (!isCreatorSubmissionOpen()) {
        setShowCreatorComingSoon(true);
        return;
      }
      if (creatorDraftInfo) {
        setPhase("creatorDraftChoice");
      } else {
        setCreatorInitialDraft(null);
        setPhase("creator");
      }
      return;
    }
    if (userDraftInfo) {
      setPhase("userDraftChoice");
    } else {
      setUserInitialDraft(null);
      setPhase("user");
    }
  };

  const handleContinueCreatorDraft = () => {
    if (!isCreatorSubmissionOpen()) {
      setShowCreatorComingSoon(true);
      return;
    }
    setCreatorInitialDraft(creatorDraftInfo?.data || null);
    setPhase("creator");
  };
  const handleStartNewCreator = () => {
    if (!isCreatorSubmissionOpen()) {
      setShowCreatorComingSoon(true);
      return;
    }
    setCreatorInitialDraft(null);
    setPhase("creator");
  };

  const handleContinueUserDraft = () => {
    setUserInitialDraft(userDraftInfo?.data || null);
    setPhase("user");
  };
  const handleStartNewUser = () => {
    setUserInitialDraft(null);
    setPhase("user");
  };

  const handleSubmitted = (role) => {
    setCompletedRole(role);
    if (role === "creator") setCreatorDraftInfo(null);
    if (role === "user") setUserDraftInfo(null);
    setPhase("success");
  };

  const handleSubmitAnother = () => {
    setCompletedRole(null);
    setCreatorInitialDraft(null);
    if (!isCreatorSubmissionOpen()) {
      setShowCreatorComingSoon(true);
      setPhase("role");
      return;
    }
    setPhase("creator");
  };

  let content = null;

  if (phase === "role") {
    content = <RoleSelect onSelect={handleRoleSelect} />;
  } else if (phase === "creatorDraftChoice" && creatorDraftInfo && isCreatorSubmissionOpen()) {
    content = (
      <DraftChoiceScreen
        roleLabel="Creator"
        draftInfo={creatorDraftInfo}
        onContinue={handleContinueCreatorDraft}
        onStartNew={handleStartNewCreator}
      />
    );
  } else if (phase === "userDraftChoice" && userDraftInfo) {
    content = (
      <DraftChoiceScreen
        roleLabel="User"
        draftInfo={userDraftInfo}
        onContinue={handleContinueUserDraft}
        onStartNew={handleStartNewUser}
      />
    );
  } else if (phase === "user") {
    content = (
      <UserSubmissionForm
        onBack={() => setPhase("role")}
        onSubmitted={() => handleSubmitted("user")}
        currentUser={currentUser}
        initialDraft={userInitialDraft}
      />
    );
  } else if (phase === "creator" && isCreatorSubmissionOpen()) {
    content = (
      <CreatorWizard
        onBack={() => setPhase("role")}
        onSubmitted={() => handleSubmitted("creator")}
        currentUser={currentUser}
        initialDraft={creatorInitialDraft}
      />
    );
  } else if (phase === "success") {
    content = (
      <SuccessScreen role={completedRole} onDone={onClose} onSubmitAnother={handleSubmitAnother} />
    );
  } else if (
    !isCreatorSubmissionOpen() &&
    (phase === "creator" || phase === "creatorDraftChoice")
  ) {
    content = <RoleSelect onSelect={handleRoleSelect} />;
  }

  return (
    <>
      {content}
      {showCreatorComingSoon && (
        <CreatorComingSoonOverlay
          onClose={() => setShowCreatorComingSoon(false)}
          currentUser={currentUser}
          onRequestSignIn={onRequestSignIn}
        />
      )}
    </>
  );
}
